import React, { useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  BsDiamondFill,
  BsArrowUpRight,
  BsCalendar4Event,
} from "react-icons/bs";
import {
  FiGift,
  FiTruck,
  FiTag,
  FiX,
  FiCheck,
  FiChevronDown,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiClock,
  FiInfo,
  FiCheckCircle,
  FiEdit3,
  FiEye,
  FiUpload,
} from "react-icons/fi";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getRewardsDashboardStats,
  getActiveRewards,
  getRewardRecentActivities,
  getWeeklyDiscountTrend,
  getWeeklyEngagementTrend,
  getAllPromoCodes,
  createPromoCode,
  getAllDiscounts,
  createCampaign,
  getAllCampaigns,
  getActiveCampaigns,
  getInactiveCampaigns,
  getExpiredCampaigns,
  getSingleCampaign,
  toggleCampaignStatus,
  updateCampaign,
  deleteCampaign,
  createDiscount,
  toggleDiscountStatus,
  deleteDiscount,
  createFreeDelivery,
  getAllFreeDeliveries,
  toggleFreeDeliveryStatus,
  deleteFreeDelivery,
  createPromoCodeFunction,
  getAllPromoCodeFunctions,
  updatePromoCodeFunction,
  deletePromoCodeFunction,
  createDiscountFunction,
  getAllDiscountFunctions,
  updateDiscountFunction,
  deleteDiscountFunction,
  toggleDiscountFunctionScope,
  createFreeDeliveryFunction,
  getAllFreeDeliveryFunctions,
  updateFreeDeliveryFunction,
  deleteFreeDeliveryFunction,
  getGamificationConfig,
  updateGamificationConfig,
  getAllCheckoutCategories,
  createCheckoutCategory,
  updateCheckoutCategory,
  toggleCheckoutCategoryItem,
  uploadFile,
} from "../../redux/thunk";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

function formatUtcCalendarDate(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { timeZone: "UTC" });
}

function toUtcYmdFromEndDate(isoOrYmd: string | undefined | null): string | null {
  if (!isoOrYmd) return null;
  const v = String(isoOrYmd).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
}

function parseWallClockTo24h(value: string): { h: number; m: number } | null {
  if (!value || typeof value !== "string") return null;
  const s = value.trim();
  if (/^\d{2}:\d{2}$/.test(s)) {
    const [hs, ms] = s.split(":");
    const h = Number(hs);
    const m = Number(ms);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return { h, m };
    return null;
  }
  const match = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (!match) return null;
  const hours12 = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3].toUpperCase();
  if (!Number.isFinite(hours12) || hours12 < 1 || hours12 > 12) return null;
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > 59) return null;
  let hours24 = hours12 % 12;
  if (meridiem === "PM") hours24 += 12;
  return { h: hours24, m: minutes };
}

function getEndTimeClockFromField(endTime: string | undefined | null): {
  h: number;
  m: number;
} | null {
  if (!endTime || typeof endTime !== "string") return null;
  const s = endTime.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s) || s.includes("T")) {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return { h: d.getUTCHours(), m: d.getUTCMinutes() };
  }
  return parseWallClockTo24h(s);
}

function getCampaignEffectiveEndMs(campaign: any): number | null {
  const endDate = campaign?.endDate;
  const endTimeField = campaign?.endTime;
  if (!endTimeField || typeof endTimeField !== "string") return null;

  const ymd = toUtcYmdFromEndDate(endDate);
  const clock = getEndTimeClockFromField(endTimeField);

  if (ymd && clock) {
    const [y, mo, d] = ymd.split("-").map(Number);
    if (Number.isFinite(y) && Number.isFinite(mo) && Number.isFinite(d)) {
      const fromParts = Date.UTC(y, mo - 1, d, clock.h, clock.m, 0, 0);
      const s = endTimeField.trim();
      let fromIso: number | null = null;
      if (s.includes("T") || /^\d{4}-\d{2}-\d{2}\s/.test(s) || s.endsWith("Z")) {
        const ms = new Date(s).getTime();
        if (!Number.isNaN(ms)) fromIso = ms;
      }
      if (fromIso != null) return Math.max(fromIso, fromParts);
      return fromParts;
    }
  }

  const t = new Date(endTimeField).getTime();
  return Number.isNaN(t) ? null : t;
}

function isCampaignExpired(campaign: any): boolean {
  const ms = getCampaignEffectiveEndMs(campaign);
  if (ms == null) return false;
  return ms < Date.now();
}

// Countdown Timer Component
const CountdownTimer = ({ endMs }: { endMs: number | null }) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (endMs == null || Number.isNaN(endMs)) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      }
      const now = Date.now();
      const difference = endMs - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds, expired: false };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endMs]);

  if (endMs == null || Number.isNaN(endMs)) {
    return <span className="text-slate-400 text-sm">—</span>;
  }

  if (timeLeft.expired) {
    return <span className="text-orange-500 font-bold text-sm">Expired</span>;
  }

  const pad = (num: number) => num.toString().padStart(2, "0");

  // Show days if more than 24 hours remaining
  if (timeLeft.days > 0) {
    return (
      <div className="flex flex-col">
        <span className="text-sm font-bold text-slate-700">
          {timeLeft.days}d {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m
        </span>
        <span className="text-[10px] text-slate-400">
          {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
        </span>
      </div>
    );
  }

  // Show hours:minutes:seconds for less than 24 hours
  const isUrgent = timeLeft.hours < 1;
  return (
    <span
      className={`font-mono font-bold text-sm ${
        isUrgent ? "text-red-500 animate-pulse" : "text-[#008B50]"
      }`}
    >
      {pad(timeLeft.hours)}:{pad(timeLeft.minutes)}:{pad(timeLeft.seconds)}
    </span>
  );
};

const SpinAndWin = () => {
  const {
    stats,
    recentActivities,
    recentActivitiesHasMore,
    recentActivitiesPagination,
    weeklyDiscountTrend,
    weeklyEngagementTrend,
    promoCodes,
    discounts,
    freeDeliveries,
    promoCodeFunctions,
    discountFunctions,
    freeDeliveryFunctions,
    checkoutCategories,
    gamificationConfig,
    loading,
  } = useAppSelector((state) => state.rewards);
  const dispatch = useAppDispatch();

  const [recentPage, setRecentPage] = useState(1);
  const recentLimit = 10;
  const [recentPagingLoading, setRecentPagingLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("Overview");
  const [moduleTab, setModuleTab] = useState("DISCOUNT");
  const [campaignFilter, setCampaignFilter] = useState<
    "all" | "active" | "inactive" | "expired"
  >("all");
  const [filteredCampaigns, setFilteredCampaigns] = useState<any[]>([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [isPromoModalVisible, setIsPromoModalVisible] = useState(false);
  const [isPromoDetailsModalOpen, setIsPromoDetailsModalOpen] = useState(false);
  const [isPromoDetailsModalVisible, setIsPromoDetailsModalVisible] =
    useState(false);
  const [promoDetailsLoading, setPromoDetailsLoading] = useState(false);
  const [promoDetailsError, setPromoDetailsError] = useState<string | null>(
    null,
  );
  const [selectedPromoCampaign, setSelectedPromoCampaign] = useState<any>(null);

  const openModal = () => {
    setIsModalOpen(true);
    setTimeout(() => setIsModalVisible(true), 10);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setTimeout(() => setIsModalOpen(false), 300);
  };

  const openPromoModal = () => {
    setIsPromoModalOpen(true);
    setTimeout(() => setIsPromoModalVisible(true), 10);
  };

  const closePromoModal = () => {
    setIsPromoModalVisible(false);
    setTimeout(() => setIsPromoModalOpen(false), 300);
  };

  const openPromoDetailsModal = async (campaign: any) => {
    setSelectedPromoCampaign(campaign);
    setPromoDetailsError(null);
    setIsPromoDetailsModalOpen(true);
    setTimeout(() => setIsPromoDetailsModalVisible(true), 10);

    const id = campaign?._id;
    if (!id) return;

    setPromoDetailsLoading(true);
    try {
      const action = await dispatch(getSingleCampaign(id));
      if (getSingleCampaign.fulfilled.match(action)) {
        const payload = (action as any)?.payload;
        const data = payload?.data ?? payload;
        if (data) {
          // Preserve populated promoCodeUsage from the list endpoint when the
          // single-campaign response returns unpopulated userId strings.
          const originalUsage = Array.isArray(campaign?.promoCodeUsage)
            ? campaign.promoCodeUsage
            : [];
          const freshUsage = Array.isArray(data.promoCodeUsage)
            ? data.promoCodeUsage
            : [];

          const hasPopulatedUsers = freshUsage.length > 0 &&
            freshUsage.some(
              (u: any) => typeof u?.userId === "object" && u?.userId !== null,
            );

          setSelectedPromoCampaign({
            ...data,
            promoCodeUsage:
              hasPopulatedUsers || freshUsage.length === 0
                ? freshUsage
                : originalUsage.length > 0
                  ? originalUsage
                  : freshUsage,
          });
        }
      } else {
        setPromoDetailsError("Failed to load promo code details");
      }
    } finally {
      setPromoDetailsLoading(false);
    }
  };

  const closePromoDetailsModal = () => {
    setIsPromoDetailsModalVisible(false);
    setTimeout(() => {
      setIsPromoDetailsModalOpen(false);
      setSelectedPromoCampaign(null);
      setPromoDetailsError(null);
    }, 300);
  };

  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isDiscountModalVisible, setIsDiscountModalVisible] = useState(false);

  const openDiscountModal = () => {
    setIsDiscountModalOpen(true);
    setTimeout(() => setIsDiscountModalVisible(true), 10);
  };

  const closeDiscountModal = () => {
    setIsDiscountModalVisible(false);
    setTimeout(() => setIsDiscountModalOpen(false), 300);
  };

  const [localCheckoutCategories, setLocalCheckoutCategories] = useState<any[]>(
    [],
  );

  const [uploadingItem, setUploadingItem] = useState<{
    cat: number;
    item: number;
  } | null>(null);

  useEffect(() => {
    if (checkoutCategories && checkoutCategories.length > 0) {
      setLocalCheckoutCategories(checkoutCategories);
    } else {
      // Default categories if none exist
      setLocalCheckoutCategories([
        {
          category: "category1",
          purchaseRange: 30000,
          isActive: true,
          items: [
            { imageUrl: "", tag: "", isActive: true },
            { imageUrl: "", tag: "", isActive: true },
            { imageUrl: "", tag: "", isActive: true },
            { imageUrl: "", tag: "", isActive: true },
          ],
        },
      ]);
    }
  }, [checkoutCategories]);

  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isModuleModalVisible, setIsModuleModalVisible] = useState(false);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [selectedModuleType, setSelectedModuleType] = useState<
    "PROMO" | "DISCOUNT" | "FREE_DELIVERY" | "MANUAL"
  >("DISCOUNT");

  const openModuleModal = (
    module?: any,
    type?: "PROMO" | "DISCOUNT" | "FREE_DELIVERY" | "MANUAL",
  ) => {
    setSelectedModule(module || null);
    if (type) setSelectedModuleType(type);
    setIsModuleModalOpen(true);
    setTimeout(() => setIsModuleModalVisible(true), 10);
  };

  const closeModuleModal = () => {
    setIsModuleModalVisible(false);
    setTimeout(() => {
      setIsModuleModalOpen(false);
      setSelectedModule(null);
    }, 300);
  };

  const [isFreeDeliveryModalOpen, setIsFreeDeliveryModalOpen] = useState(false);
  const [isFreeDeliveryModalVisible, setIsFreeDeliveryModalVisible] =
    useState(false);

  const openFreeDeliveryModal = () => {
    setIsFreeDeliveryModalOpen(true);
    setTimeout(() => setIsFreeDeliveryModalVisible(true), 10);
  };

  const closeFreeDeliveryModal = () => {
    setIsFreeDeliveryModalVisible(false);
    setTimeout(() => setIsFreeDeliveryModalOpen(false), 300);
  };

  // Discount Scope Modal
  const [discountScopeModalOpen, setDiscountScopeModalOpen] = useState(false);
  const [discountScopeModalVisible, setDiscountScopeModalVisible] =
    useState(false);
  const [selectedDiscountForScope, setSelectedDiscountForScope] =
    useState<any>(null);

  const openDiscountScopeModal = (discount: any) => {
    setSelectedDiscountForScope(discount);
    setDiscountScopeModalOpen(true);
    setTimeout(() => setDiscountScopeModalVisible(true), 10);
  };

  const closeDiscountScopeModal = () => {
    setDiscountScopeModalVisible(false);
    setTimeout(() => {
      setDiscountScopeModalOpen(false);
      setSelectedDiscountForScope(null);
    }, 300);
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [itemToDeleteType, setItemToDeleteType] = useState<
    | "DISCOUNT"
    | "FREE_DELIVERY"
    | "PROMO"
    | "CAMPAIGN"
    | "DISCOUNT_FUNCTION"
    | "FREE_DELIVERY_FUNCTION"
    | "PROMO_FUNCTION"
  >("PROMO");

  const openDeleteModal = (
    id: string,
    type:
      | "DISCOUNT"
      | "FREE_DELIVERY"
      | "PROMO"
      | "CAMPAIGN"
      | "DISCOUNT_FUNCTION"
      | "FREE_DELIVERY_FUNCTION"
      | "PROMO_FUNCTION" = "PROMO",
  ) => {
    setItemToDelete(id);
    setItemToDeleteType(type);
    setDeleteModalOpen(true);
    setTimeout(() => setDeleteModalVisible(true), 10);
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setTimeout(() => {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }, 300);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      let resultAction;
      if (itemToDeleteType === "DISCOUNT") {
        resultAction = await dispatch(deleteDiscount(itemToDelete));
      } else if (itemToDeleteType === "FREE_DELIVERY") {
        resultAction = await dispatch(deleteFreeDelivery(itemToDelete));
      } else if (itemToDeleteType === "CAMPAIGN") {
        resultAction = await dispatch(deleteCampaign(itemToDelete));
      } else if (itemToDeleteType === "DISCOUNT_FUNCTION") {
        resultAction = await dispatch(deleteDiscountFunction(itemToDelete));
      } else if (itemToDeleteType === "FREE_DELIVERY_FUNCTION") {
        resultAction = await dispatch(deleteFreeDeliveryFunction(itemToDelete));
      } else if (itemToDeleteType === "PROMO_FUNCTION") {
        resultAction = await dispatch(deletePromoCodeFunction(itemToDelete));
      } else {
        closeDeleteModal();
        return;
      }

      // Refresh the list after successful deletion
      if (
        deleteDiscount.fulfilled.match(resultAction) ||
        deleteFreeDelivery.fulfilled.match(resultAction) ||
        deleteCampaign.fulfilled.match(resultAction)
      ) {
        dispatch(getAllPromoCodes({ page: 1, limit: 100 }));
        dispatch(getAllDiscounts());
        dispatch(getAllFreeDeliveries());
        if (itemToDeleteType === "CAMPAIGN") {
          fetchFilteredCampaigns(campaignFilter);
        }
      }
      closeDeleteModal();
    }
  };

  const handleToggleCampaign = (id: string, currentStatus: boolean) => {
    dispatch(
      toggleCampaignStatus({ campaignId: id, isActive: !currentStatus }),
    );
  };

  const handleDeleteCampaign = (id: string) => {
    if (window.confirm("Are you sure you want to delete this campaign?")) {
      dispatch(deleteCampaign(id));
    }
  };

  const handleToggleDiscount = (id: string, currentStatus: boolean) => {
    dispatch(
      toggleDiscountStatus({ discountId: id, isActive: !currentStatus }),
    );
  };

  const handleDeleteDiscount = (id: string) => {
    openDeleteModal(id, "DISCOUNT");
  };

  const handleToggleFreeDelivery = (id: string, currentStatus: boolean) => {
    dispatch(
      toggleFreeDeliveryStatus({
        freeDeliveryId: id,
        isActive: !currentStatus,
      }),
    );
  };

  const handleDeleteFreeDelivery = (id: string) => {
    openDeleteModal(id, "FREE_DELIVERY");
  };

  useEffect(() => {
    dispatch(getRewardsDashboardStats());
    dispatch(getActiveRewards());
    dispatch(getRewardRecentActivities({ page: 1, limit: recentLimit }));
    dispatch(getWeeklyDiscountTrend());
    dispatch(getWeeklyEngagementTrend());
    dispatch(getAllPromoCodes({ page: 1, limit: 100 }));
    dispatch(getAllDiscounts());
    dispatch(getAllCampaigns());
    dispatch(getAllFreeDeliveries());
    dispatch(getAllFreeDeliveries());
    dispatch(getAllPromoCodeFunctions());
    dispatch(getAllDiscountFunctions());
    dispatch(getAllFreeDeliveryFunctions());
    dispatch(getGamificationConfig());
    dispatch(getAllCheckoutCategories());
  }, [dispatch, recentLimit]);

  const handleGotoRecentActivitiesPage = async (page: number) => {
    if (recentPagingLoading) return;

    setRecentPagingLoading(true);
    try {
      await dispatch(getRewardRecentActivities({ page, limit: recentLimit }));
      setRecentPage(page);
    } finally {
      setRecentPagingLoading(false);
    }
  };

  const totalRecentPages = recentActivitiesPagination?.totalPages;

  const hasRecentPrev = recentPage > 1;
  const hasRecentNext =
    typeof totalRecentPages === "number" && totalRecentPages > 0
      ? recentPage < totalRecentPages
      : recentActivitiesHasMore;

  // Fetch campaigns based on filter
  const fetchFilteredCampaigns = async (
    filter: "all" | "active" | "inactive" | "expired",
  ) => {
    setCampaignLoading(true);
    try {
      let resultAction;
      switch (filter) {
        case "active":
          resultAction = await dispatch(getActiveCampaigns());
          break;
        case "inactive":
          resultAction = await dispatch(getInactiveCampaigns());
          break;
        case "expired":
          resultAction = await dispatch(getExpiredCampaigns());
          break;
        default:
          resultAction = await dispatch(getAllCampaigns());
      }
      if (resultAction.payload?.data) {
        setFilteredCampaigns(resultAction.payload.data);
      } else if (Array.isArray(resultAction.payload)) {
        setFilteredCampaigns(resultAction.payload);
      }
    } finally {
      setCampaignLoading(false);
    }
  };

  // Fetch campaigns when filter changes
  useEffect(() => {
    fetchFilteredCampaigns(campaignFilter);
  }, [campaignFilter]);

  const metrics = [
    {
      title: "Spins Triggered",
      value: stats?.spinsTriggered?.toLocaleString() || "",
      change: "",
      icon: <BsDiamondFill className="text-white w-5 h-5" />,
      iconBg: "bg-[#008B50]",
    },
    {
      title: "Rewards Used",
      value: stats?.rewardUsed?.toLocaleString() || "",
      change: "",
      icon: <FiGift className="text-white w-5 h-5" />,
      iconBg: "bg-[#FF9500]",
    },
    {
      title: "Free Deliveries",
      value: stats?.freeDeliveryUsed?.toLocaleString() || "",
      change: "",
      icon: <FiTruck className="text-white w-5 h-5" />,
      iconBg: "bg-[#0047AB]",
    },
    {
      title: "Promo Usage",
      value: stats?.promoCodeUsage?.toLocaleString() || "",
      change: "",
      icon: <FiTag className="text-white w-5 h-5" />,
      iconBg: "bg-[#D4A017]",
    },
  ];

  const tabs = [
    "Overview",
    "Modules",
    "Gift Spin",
    "Promo codes",
    "Discounts",
    "Free Deliveries",
    "Gamification",
  ];

  const renderOverview = () => {
    const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const barChartData = {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"],
      datasets: [
        {
          label: "Total Discount Used",
          data: days.map(
            (d) => weeklyDiscountTrend?.[d]?.totalDiscountUsed || 0,
          ),
          backgroundColor: "#FF9500",
          borderRadius: 20,
          barThickness: 8,
        },
        {
          label: "Total Reward Value",
          data: days.map(
            (d) => weeklyDiscountTrend?.[d]?.totalRewardValue || 0,
          ),
          backgroundColor: "#22C55E",
          borderRadius: 20,
          barThickness: 8,
        },
      ],
    };

    const lineChartData = {
      labels: ["Sun", "Mon", "Tue", "Wed", "Thur", "Fri", "Sat"],
      datasets: [
        {
          label: "Free Deliveries",
          data: days.map(
            (d) => weeklyEngagementTrend?.[d]?.freeDeliveries || 0,
          ),
          borderColor: "#0F172A",
          backgroundColor: "rgba(15, 23, 42, 0.05)",
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "#0F172A",
          borderWidth: 2,
        },
        {
          label: "Promo Usage",
          data: days.map(
            (d) => weeklyEngagementTrend?.[d]?.promoCodeUsage || 0,
          ),
          borderColor: "#FF9500",
          backgroundColor: "transparent",
          tension: 0.4,
          pointRadius: 4,
          pointBackgroundColor: "#FF9500",
          borderWidth: 2,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#fff",
          titleColor: "#1e293b",
          bodyColor: "#1e293b",
          borderColor: "#e2e8f0",
          borderWidth: 1,
          padding: 12,
          boxPadding: 4,
          usePointStyle: true,
          callbacks: {
            label: (context: any) =>
              ` ${context.dataset.label}: ${context.parsed.y}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { drawBorder: false, color: "rgba(0,0,0,0.05)" },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
        x: {
          grid: { display: false },
          ticks: { color: "#94a3b8", font: { size: 11 } },
        },
      },
    };

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
            <h3 className="text-xl font-bold text-slate-800 mb-8">
              Weekly Activity
            </h3>
            <div className="h-72">
              <Bar data={barChartData} options={chartOptions} />
            </div>
          </section>
          <section className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-bold text-slate-800">
                Engagement Trend
              </h3>
              {/* <select className="text-sm font-bold border border-slate-200 bg-slate-50 rounded-xl px-4 py-2 text-slate-600 outline-none">
                <option>This Week</option>
              </select> */}
            </div>
            <div className="h-72">
              <Line data={lineChartData} options={chartOptions} />
            </div>
            <div className="flex justify-center gap-8 mt-6">
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full bg-[#0F172A]"></div>
                <span className="text-xs text-slate-500 font-bold uppercase">
                  Free Deliveries
                </span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full bg-[#FF9500]"></div>
                <span className="text-xs text-slate-500 font-bold uppercase">
                  Promo Usage
                </span>
              </div>
            </div>
          </section>
        </div>

        <section className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
          <h3 className="text-xl font-bold text-slate-800 mb-8">
            Recent Rewards Activity
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-50">
                  <th className="pb-4">S/N</th>
                  <th className="pb-4">User</th>
                  <th className="pb-4">Reward Type</th>
                  <th className="pb-4">Value</th>
                  <th className="pb-4">Status</th>
                  <th className="pb-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {recentActivities.map((activity, index) => (
                  <tr key={index} className="text-sm">
                    <td className="py-4 text-slate-500">
                      {(recentPage - 1) * recentLimit + index + 1}
                    </td>
                    <td className="py-4 font-bold text-slate-700">
                      {activity.userName}
                    </td>
                    <td className="py-4 text-slate-600 font-medium">
                      {activity.rewardType}
                    </td>
                    <td className="py-4 font-bold text-slate-800">
                      {activity.value || "N/A"}
                    </td>
                    <td className="py-4">
                      <span
                        className={`px-6 py-1.5 rounded-lg text-xs font-bold inline-block min-w-[100px] text-center ${
                          activity.status === "used" ||
                          activity.status === "Used"
                            ? "bg-[#008B50] text-white"
                            : activity.status === "active" ||
                                activity.status === "Active"
                              ? "bg-[#E6F7F0] text-[#008B50]"
                              : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {activity.status}
                      </span>
                    </td>
                    <td className="py-4 text-slate-500 font-medium">
                      {new Date(activity.date).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-3 mt-6">
            {recentPagingLoading && (
              <span className="text-sm font-bold text-slate-400">Loading…</span>
            )}

            <button
              type="button"
              onClick={() => handleGotoRecentActivitiesPage(recentPage - 1)}
              disabled={!hasRecentPrev || recentPagingLoading}
              className={`px-6 py-3 rounded-xl text-sm font-bold border transition ${
                !hasRecentPrev || recentPagingLoading
                  ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              Previous
            </button>

            <button
              type="button"
              onClick={() => handleGotoRecentActivitiesPage(recentPage + 1)}
              disabled={!hasRecentNext || recentPagingLoading}
              className={`px-6 py-3 rounded-xl text-sm font-bold border transition ${
                !hasRecentNext || recentPagingLoading
                  ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
              }`}
            >
              Next
            </button>
          </div>
        </section>
      </div>
    );
  };

  const renderGamificationTab = () => {
    if (!gamificationConfig) return <LoadingSpinner />;

    // Helper to safely access nested properties
    const getConfigItem = (key: keyof typeof gamificationConfig) => {
      const item = gamificationConfig[key];
      // Check if it's an object and return it, otherwise return an empty object
      return item && typeof item === "object" ? item : ({} as any);
    };

    const handleToggle = (key: string, enabled: boolean) => {
      dispatch(updateGamificationConfig({ [key]: { enabled } }));
    };

    const handleUpdate = (key: string, field: string, value: any) => {
      const currentConfig = getConfigItem(
        key as keyof typeof gamificationConfig,
      );
      dispatch(
        updateGamificationConfig({
          [key]: { ...currentConfig, [field]: value },
        }),
      );
    };

    const isChecked = (key: keyof typeof gamificationConfig) => {
      return !!getConfigItem(key).enabled;
    };

    // Reusable duration picker — local state, only fires API on blur
    const DurationPicker = ({
      totalSeconds,
      accentColor,
      onCommit,
    }: {
      totalSeconds: number;
      accentColor: string;
      onCommit: (secs: number) => void;
    }) => {
      const [local, setLocal] = React.useState({
        h: Math.floor(totalSeconds / 3600),
        m: Math.floor((totalSeconds % 3600) / 60),
        s: totalSeconds % 60,
      });

      React.useEffect(() => {
        setLocal({
          h: Math.floor(totalSeconds / 3600),
          m: Math.floor((totalSeconds % 3600) / 60),
          s: totalSeconds % 60,
        });
      }, [totalSeconds]);

      const commit = () => {
        onCommit(local.h * 3600 + local.m * 60 + local.s);
      };

      return (
        <div className="flex gap-2 items-center">
          {(
            [
              { label: "hrs", key: "h" as const, max: undefined },
              { label: "mins", key: "m" as const, max: 59 },
              { label: "secs", key: "s" as const, max: 59 },
            ] as const
          ).map((unit, idx) => (
            <React.Fragment key={unit.key}>
              {idx > 0 && (
                <span className="font-bold text-slate-400 pb-4">:</span>
              )}
              <div className="flex-1 space-y-1">
                <input
                  type="number"
                  min={0}
                  max={unit.max}
                  value={local[unit.key]}
                  onChange={(e) => {
                    const raw = Math.max(0, Number(e.target.value));
                    const val =
                      unit.max !== undefined ? Math.min(unit.max, raw) : raw;
                    setLocal((prev) => ({ ...prev, [unit.key]: val }));
                  }}
                  onBlur={commit}
                  className="w-full p-4 rounded-2xl bg-slate-50 border border-slate-100 focus:bg-white outline-none transition-all font-bold text-slate-800 text-center"
                  style={{ borderColor: undefined }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = accentColor)
                  }
                  onBlurCapture={(e) =>
                    (e.currentTarget.style.borderColor = "")
                  }
                />
                <p className="text-[10px] text-slate-400 text-center font-medium">
                  {unit.label}
                </p>
              </div>
            </React.Fragment>
          ))}
        </div>
      );
    };

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-20 text-left">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Checkout Category */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4 text-left">
                <div className="bg-[#0047AB] p-4 rounded-2xl text-white">
                  <FiCheckCircle className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 text-lg">
                    Checkout Category
                  </h3>
                  <p className="text-xs text-slate-500">
                    Categorize users at checkout
                  </p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isChecked("checkoutCategory")}
                  onChange={(e) =>
                    handleToggle("checkoutCategory", e.target.checked)
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>

          {/* Promo Code */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4 text-left">
                <div className="bg-[#D4A017] p-4 rounded-2xl text-white">
                  <FiTag className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 text-lg">
                    Promo Code
                  </h3>
                  <p className="text-xs text-slate-500">
                    Enable or disable promo code feature
                  </p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isChecked("promoCode")}
                  onChange={(e) => handleToggle("promoCode", e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
            </div>
          </div>

          {/* Discount Spin */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-8 md:col-span-2">
            <div className="flex justify-between items-center border-b border-slate-50 pb-6">
              <div className="flex items-center gap-4 text-left">
                <div className="bg-[#FF9500] p-4 rounded-2xl text-white">
                  <FiTag className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 text-lg">
                    Discount Spin
                  </h3>
                  <p className="text-xs text-slate-500">
                    Custom spin delay and duration
                  </p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isChecked("discountSpin")}
                  onChange={(e) =>
                    handleToggle("discountSpin", e.target.checked)
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-2">
              <div className="space-y-3 text-left">
                <label className="text-sm font-bold text-slate-600 block">
                  Delay Duration
                </label>
                <DurationPicker
                  totalSeconds={getConfigItem("discountSpin").delaySeconds ?? 0}
                  accentColor="#FF9500"
                  onCommit={(secs) =>
                    handleUpdate("discountSpin", "delaySeconds", secs)
                  }
                />
                <p className="text-[10px] text-slate-400 font-medium">
                  Time to wait before showing the spin after checkout (
                  {getConfigItem("discountSpin").delaySeconds ?? 0}s total)
                </p>
              </div>
              <div className="space-y-3 text-left">
                <label className="text-sm font-bold text-slate-600 block">
                  Availability Duration
                </label>
                <DurationPicker
                  totalSeconds={
                    getConfigItem("discountSpin").durationSeconds ?? 0
                  }
                  accentColor="#FF9500"
                  onCommit={(secs) =>
                    handleUpdate("discountSpin", "durationSeconds", secs)
                  }
                />
                <p className="text-[10px] text-slate-400 font-medium">
                  How long the discount spin remains available (
                  {getConfigItem("discountSpin").durationSeconds ?? 0}s total)
                </p>
              </div>
            </div>
          </div>

          {/* Free Delivery */}
          <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-8 md:col-span-2">
            <div className="flex justify-between items-center border-b border-slate-50 pb-6">
              <div className="flex items-center gap-4 text-left">
                <div className="bg-[#004D40] p-4 rounded-2xl text-white">
                  <FiTruck className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-800 text-lg">
                    Free Delivery
                  </h3>
                  <p className="text-xs text-slate-500">
                    Configure free delivery duration
                  </p>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isChecked("freeDelivery")}
                  onChange={(e) =>
                    handleToggle("freeDelivery", e.target.checked)
                  }
                />
                <span className="slider round"></span>
              </label>
            </div>

            <div className="space-y-3 pt-2 text-left">
              <label className="text-sm font-bold text-slate-600 block">
                Availability Duration
              </label>
              <DurationPicker
                totalSeconds={
                  getConfigItem("freeDelivery").durationSeconds ?? 0
                }
                accentColor="#004D40"
                onCommit={(secs) =>
                  handleUpdate("freeDelivery", "durationSeconds", secs)
                }
              />
              <p className="text-[10px] text-slate-400 font-medium">
                Validity period of the free delivery reward (
                {getConfigItem("freeDelivery").durationSeconds ?? 0}s total)
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderModules = () => {
    const handleToggleStatus = (
      id: string,
      currentStatus: boolean,
      type: "DISCOUNT" | "FREE_DELIVERY" | "PROMO",
    ) => {
      if (type === "DISCOUNT") {
        dispatch(
          updateDiscountFunction({
            functionId: id,
            payload: { isActive: !currentStatus },
          }),
        );
      } else if (type === "FREE_DELIVERY") {
        dispatch(
          updateFreeDeliveryFunction({
            functionId: id,
            payload: { isActive: !currentStatus },
          }),
        );
      } else {
        dispatch(
          updatePromoCodeFunction({
            functionId: id,
            payload: { isActive: !currentStatus },
          }),
        );
      }
    };

    const handleDelete = (
      id: string,
      type: "DISCOUNT" | "FREE_DELIVERY" | "PROMO",
    ) => {
      // Map module tab type to function delete type
      const functionType =
        type === "DISCOUNT"
          ? "DISCOUNT_FUNCTION"
          : type === "FREE_DELIVERY"
            ? "FREE_DELIVERY_FUNCTION"
            : "PROMO_FUNCTION";
      openDeleteModal(id, functionType);
    };

    const modules =
      moduleTab === "DISCOUNT"
        ? discountFunctions
        : moduleTab === "FREE_DELIVERY"
          ? freeDeliveryFunctions
          : promoCodeFunctions;

    const getModuleIcon = (scope: string, type: string) => {
      if (type === "FREE_DELIVERY") return <FiTruck className="text-white" />;
      if (scope === "Percentage Discount")
        return <FiTag className="text-white" />;
      return <FiGift className="text-white" />;
    };

    const getModuleColor = (scope: string, type: string) => {
      if (type === "FREE_DELIVERY") return "bg-[#0047AB]";
      if (scope === "Percentage Discount") return "bg-[#FF9500]";
      return "bg-[#004D40]";
    };

    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-bold text-slate-800">
            Enable/Disable Modules
          </h3>
          <button
            onClick={() => openModuleModal(null, moduleTab as any)}
            disabled={loading}
            className="bg-[#008B50] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create New Module
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-6 w-fit">
          {["DISCOUNT", "FREE_DELIVERY", "PROMO"].map((tab) => (
            <button
              key={tab}
              onClick={() => setModuleTab(tab as any)}
              className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
                moduleTab === tab
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-500 hover:text-slate-600"
              }`}
            >
              {tab === "DISCOUNT"
                ? "Discounts"
                : tab === "FREE_DELIVERY"
                  ? "Free Deliveries"
                  : "Promo Codes"}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-4">
            {modules.map((m) => (
              <div
                key={m._id}
                className="bg-white p-6 rounded-[1.5rem] border border-slate-100 shadow-sm flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`${getModuleColor(
                      m.scope,
                      moduleTab,
                    )} p-4 rounded-xl`}
                  >
                    {getModuleIcon(m.scope, moduleTab)}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800">{m.scope}</h4>
                    {moduleTab !== "FREE_DELIVERY" && (
                      <p className="text-sm text-slate-500 font-medium tracking-tight">
                        Value: {(m as any).value}
                        {m.scope === "Percentage Discount" ? "%" : " ₦"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <label className="switch scale-90">
                    <input
                      type="checkbox"
                      checked={m.isActive}
                      onChange={() =>
                        handleToggleStatus(m._id, m.isActive, moduleTab as any)
                      }
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleDelete(m._id, moduleTab as any)}
                      className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => openModuleModal(m, moduleTab as any)}
                      className="p-3 bg-green-50 text-[#008B50] rounded-xl hover:bg-green-100 transition-colors"
                    >
                      <FiEdit3 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {modules.length === 0 && (
              <div className="text-center py-20 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                <p className="text-slate-400 font-bold">
                  No modules created yet
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGiftSpinTab = () => {
    const handleCategoryChange = (index: number, field: string, value: any) => {
      const updated = [...localCheckoutCategories];
      updated[index] = { ...updated[index], [field]: value };
      setLocalCheckoutCategories(updated);
    };

    const handleItemChange = (
      catIndex: number,
      itemIndex: number,
      field: string,
      value: any,
    ) => {
      const updated = [...localCheckoutCategories];
      const items = [...updated[catIndex].items];
      items[itemIndex] = { ...items[itemIndex], [field]: value };
      updated[catIndex] = { ...updated[catIndex], items };
      setLocalCheckoutCategories(updated);
    };

    const handleToggleItemAdminActive = async (
      catIndex: number,
      itemIndex: number,
      categoryId: string,
      itemId: string,
      currentStatus: boolean,
    ) => {
      // Update local state first for immediate feedback
      handleItemChange(catIndex, itemIndex, "isAdminActive", !currentStatus);

      // Call API
      const resultAction = await dispatch(
        toggleCheckoutCategoryItem({
          categoryId,
          itemId,
          isAdminActive: !currentStatus,
        }),
      );
      if (toggleCheckoutCategoryItem.fulfilled.match(resultAction)) {
        // Refresh to sync with server
        dispatch(getAllCheckoutCategories());
      } else {
        // Revert on failure
        handleItemChange(catIndex, itemIndex, "isAdminActive", currentStatus);
      }
    };

    const handleFileUpload = async (
      e: React.ChangeEvent<HTMLInputElement>,
      catIdx: number,
      itemIdx: number,
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploadingItem({ cat: catIdx, item: itemIdx });
      try {
        const resultAction = await dispatch(
          uploadFile({ file, type: "image" }),
        );
        if (uploadFile.fulfilled.match(resultAction)) {
          handleItemChange(
            catIdx,
            itemIdx,
            "imageUrl",
            resultAction.payload.url,
          );
          toast.success("Image uploaded successfully");
        } else {
          toast.error(
            (resultAction.payload as string) || "Failed to upload image",
          );
        }
      } catch (err) {
        toast.error("An error occurred during upload");
      } finally {
        setUploadingItem(null);
      }
    };

    const saveConfiguration = async () => {
      try {
        const savePromises = localCheckoutCategories.map((cat) => {
          const { _id, adminId, createdAt, updatedAt, __v, ...payload } = cat;
          // Ensure items don't have _ids either for a clean update
          const cleanPayload = {
            ...payload,
            items: payload.items?.map((item: any) => {
              const { _id: itemId, ...itemPayload } = item;
              return itemPayload;
            }),
          };

          // If category has _id, use PATCH to update; otherwise use POST to create
          if (_id) {
            return dispatch(
              updateCheckoutCategory({
                categoryId: _id,
                payload: cleanPayload,
              }),
            );
          } else {
            return dispatch(createCheckoutCategory(cleanPayload as any));
          }
        });

        await Promise.all(savePromises);
        toast.success("Configuration saved successfully");
        dispatch(getAllCheckoutCategories());
      } catch (err) {
        toast.error("Failed to save configuration");
      }
    };

    const addNewCategory = () => {
      if (localCheckoutCategories.length >= 4) {
        toast.error("Maximum 4 categories allowed");
        return;
      }
      const newCategory = {
        category: `category${localCheckoutCategories.length + 1}`,
        purchaseRange: 30000,
        isActive: true,
        items: [
          { imageUrl: "", tag: "", isActive: true },
          { imageUrl: "", tag: "", isActive: true },
          { imageUrl: "", tag: "", isActive: true },
          { imageUrl: "", tag: "", isActive: true },
        ],
      };
      setLocalCheckoutCategories([...localCheckoutCategories, newCategory]);
    };

    const removeCategory = (index: number) => {
      if (localCheckoutCategories.length <= 1) {
        toast.error("At least 1 category is required");
        return;
      }
      const updated = [...localCheckoutCategories];
      updated.splice(index, 1);
      setLocalCheckoutCategories(updated);
    };

    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        <header className="flex justify-between items-end">
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              Post-Checkout Gift Spin Configuration
            </h3>
            <p className="text-sm text-slate-500">
              Configure up to 4 categories with purchase thresholds and gift
              items
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold text-slate-500">
              {localCheckoutCategories.length}/4 Categories
            </span>
            <button
              onClick={addNewCategory}
              disabled={localCheckoutCategories.length >= 4}
              className="bg-[#008B50] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <FiPlus className="w-5 h-5" />
              Add Category
            </button>
          </div>
        </header>

        <div className="space-y-12">
          {localCheckoutCategories.map((cat, catIdx) => (
            <div
              key={catIdx}
              className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-10"
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="bg-[#004D40] text-white w-14 h-14 rounded-xl flex items-center justify-center font-bold text-2xl uppercase">
                    {cat.category?.[0] || "A"}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 text-lg">
                      {cat.category?.toUpperCase() || "NEW CATEGORY"}
                    </h4>
                    <p className="text-xs text-slate-400">
                      Target Range: ₦{(cat.purchaseRange || 0).toLocaleString()}
                      +
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => removeCategory(catIdx)}
                    disabled={localCheckoutCategories.length <= 1}
                    className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Remove category"
                  >
                    <FiTrash2 className="w-5 h-5" />
                  </button>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={cat.isActive}
                      onChange={(e) =>
                        handleCategoryChange(
                          catIdx,
                          "isActive",
                          e.target.checked,
                        )
                      }
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500">
                    Category Name
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      value={cat.category}
                      onChange={(e) =>
                        handleCategoryChange(catIdx, "category", e.target.value)
                      }
                      className="w-full p-5 rounded-2xl bg-slate-50/50 border border-slate-100 focus:bg-white focus:border-[#008B50] outline-none transition-all font-bold text-slate-800"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500">
                    Min Purchase Range
                  </label>
                  <div className="relative group">
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 font-bold">
                      ₦
                    </span>
                    <input
                      type="number"
                      value={cat.purchaseRange}
                      onChange={(e) =>
                        handleCategoryChange(
                          catIdx,
                          "purchaseRange",
                          Number(e.target.value),
                        )
                      }
                      className="w-full p-5 pl-12 rounded-2xl bg-slate-50/50 border border-slate-100 focus:bg-white focus:border-[#008B50] outline-none transition-all font-bold text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {cat.items.map((item: any, itemIdx: number) => (
                  <div
                    key={itemIdx}
                    className={`relative p-8 rounded-[2rem] border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                      !item.tag
                        ? "border-slate-100 bg-slate-50/20"
                        : "border-[#E6F7F0] bg-[#E6F7F0]/20"
                    }`}
                  >
                    <div className="absolute top-4 right-6 flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-slate-400">
                          {item.isActive ? "Active" : "Off"}
                        </span>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={item.isActive}
                            onChange={(e) =>
                              handleItemChange(
                                catIdx,
                                itemIdx,
                                "isActive",
                                e.target.checked,
                              )
                            }
                          />
                          <span className="slider round"></span>
                        </label>
                      </div>
                      {cat._id && item._id && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-blue-400">
                            {item.isAdminActive !== false
                              ? "Admin On"
                              : "Admin Off"}
                          </span>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={item.isAdminActive !== false}
                              onChange={() =>
                                handleToggleItemAdminActive(
                                  catIdx,
                                  itemIdx,
                                  cat._id,
                                  item._id,
                                  item.isAdminActive !== false,
                                )
                              }
                            />
                            <span className="slider round"></span>
                          </label>
                        </div>
                      )}
                    </div>
                    <span className="absolute top-4 left-6 text-[10px] font-extrabold text-slate-400 bg-white px-2 py-0.5 rounded-lg border border-slate-100">
                      Q{itemIdx + 1}
                    </span>
                    <div className="w-full space-y-4">
                      <div className="flex flex-col items-center gap-3 w-full">
                        <div className="relative group/img">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.tag}
                              className="w-20 h-20 object-contain rounded-xl"
                            />
                          ) : (
                            <div className="w-20 h-20 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-100">
                              <FiGift className="text-slate-300 w-8 h-8" />
                            </div>
                          )}

                          {uploadingItem?.cat === catIdx &&
                          uploadingItem?.item === itemIdx ? (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#008B50]"></div>
                            </div>
                          ) : (
                            <label className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity rounded-xl flex items-center justify-center cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) =>
                                  handleFileUpload(e, catIdx, itemIdx)
                                }
                              />
                              <FiUpload className="text-white w-6 h-6" />
                            </label>
                          )}
                        </div>

                        <input
                          type="text"
                          placeholder="Item Name (e.g. Mug)"
                          value={item.tag}
                          onChange={(e) =>
                            handleItemChange(
                              catIdx,
                              itemIdx,
                              "tag",
                              e.target.value,
                            )
                          }
                          className="w-full text-center bg-white/50 p-2 rounded-lg border border-transparent focus:border-[#008B50] focus:bg-white outline-none font-bold text-slate-800 transition-all"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="fixed bottom-0 left-[260px] right-4 bg-white/80 backdrop-blur-xl border-t border-slate-100 p-8 flex justify-center gap-6 z-50">
          <button
            onClick={saveConfiguration}
            disabled={loading}
            className="px-20 py-4 bg-[#008B50] text-white rounded-xl font-bold shadow-lg shadow-green-900/10 hover:bg-[#007041] transition-all disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save Configuration"}
          </button>
          <button
            onClick={() => dispatch(getAllCheckoutCategories())}
            className="px-20 py-4 bg-[#E6F7F0] text-[#008B50] rounded-xl font-bold hover:bg-[#d5eedf] transition-all"
          >
            Reset Changes
          </button>
        </div>
      </div>
    );
  };

  const handleTogglePromoCode = (promoId: string, currentStatus: boolean) => {
    dispatch(
      toggleCampaignStatus({ campaignId: promoId, isActive: !currentStatus }),
    );
  };

  const handleDeletePromoCode = (promoId: string) => {
    openDeleteModal(promoId, "CAMPAIGN");
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008B50]"></div>
    </div>
  );

  const renderPromoCodesTab = () => {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              Promo Code Manager
            </h3>
            <p className="text-sm text-slate-500">
              Create, manage, and track promotional codes
            </p>
          </div>
          <button
            onClick={openPromoModal}
            disabled={loading}
            className="bg-[#008B50] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Promo Code
          </button>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-8">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search Promo codes"
                className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-100 bg-slate-50/50 outline-none focus:bg-white focus:border-[#008B50] transition-all"
              />
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
            <div className="relative w-48">
              <select
                value={campaignFilter}
                onChange={(e) =>
                  setCampaignFilter(
                    e.target.value as "all" | "active" | "inactive" | "expired",
                  )
                }
                className="w-full px-6 py-4 rounded-xl border border-slate-100 bg-slate-50/50 outline-none appearance-none font-bold text-slate-700 cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
              <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {campaignLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-50">
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Time Left
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="pb-4 text-right text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filteredCampaigns.map((campaign: any) => {
                    const isExpired = isCampaignExpired(campaign);
                    const effectiveEndMs = getCampaignEffectiveEndMs(campaign);
                    const statusLabel = isExpired
                      ? "Expired"
                      : campaign.isActive
                        ? "Active"
                        : "Inactive";
                    const statusClass = isExpired
                      ? "bg-orange-50 text-orange-500"
                      : campaign.isActive
                        ? "bg-[#E6F7F0] text-[#008B50]"
                        : "bg-red-50 text-red-500";

                    return (
                      <tr
                        key={campaign._id}
                        className="group hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="py-5">
                          <span className="bg-slate-50 px-3 py-1.5 rounded-lg text-sm font-bold text-slate-700">
                            {campaign.promoCode}
                          </span>
                        </td>
                        <td className="py-5">
                          <span className="text-sm font-bold text-slate-700">
                            {campaign.campaign}
                          </span>
                        </td>
                        <td className="py-5 text-sm text-slate-500 max-w-[200px] truncate">
                          {campaign.description || "N/A"}
                        </td>
                        <td className="py-5 text-sm font-bold text-slate-700">
                          {formatUtcCalendarDate(campaign.startDate)}
                        </td>
                        <td className="py-5 text-sm font-bold text-slate-700">
                          {formatUtcCalendarDate(campaign.endDate)}
                        </td>
                        <td className="py-5">
                          <CountdownTimer endMs={effectiveEndMs} />
                        </td>
                        <td className="py-5 text-sm font-bold text-slate-700">
                          {Array.isArray(campaign.promoCodeUsage)
                            ? campaign.promoCodeUsage.length
                            : 0}
                        </td>
                        <td className="py-5">
                          <span
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold ${statusClass}`}
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td className="py-5 text-right">
                          <div className="flex justify-end items-center gap-4">
                            <label className="switch scale-75 transform-gpu">
                              <input
                                type="checkbox"
                                checked={!isExpired && campaign.isActive}
                                onChange={() => {
                                  if (isExpired) {
                                    toast.error(
                                      "Update end date & time in details, then enable.",
                                    );
                                    return;
                                  }
                                  dispatch(
                                    toggleCampaignStatus({
                                      campaignId: campaign._id,
                                      isActive: !campaign.isActive,
                                    }),
                                  ).then(() =>
                                    fetchFilteredCampaigns(campaignFilter),
                                  );
                                }}
                              />
                              <span className="slider round"></span>
                            </label>
                            <button
                              onClick={() => openPromoDetailsModal(campaign)}
                              className="text-slate-400 hover:text-slate-700 transition-colors"
                            >
                              <FiEye className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredCampaigns.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="py-20 text-center text-slate-400 font-bold"
                      >
                        No campaigns found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDiscountsTab = () => {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              Discount Manager
            </h3>
            <p className="text-sm text-slate-500">
              Track and manage active discounts
            </p>
          </div>
          <button
            onClick={openDiscountModal}
            disabled={loading}
            className="bg-[#008B50] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Discount
          </button>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-8">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-50">
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Discount Name
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Target
                    </th>
                    <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">
                      Status & Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {discounts.map((discount, idx: number) => (
                    <tr
                      key={discount._id || idx}
                      className="group hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-5 font-bold text-slate-700">
                        {discount.campaign || discount.name}
                      </td>
                      <td className="py-5 text-sm text-slate-500 max-w-xs truncate">
                        {discount.description || "N/A"}
                      </td>
                      <td className="py-5 text-sm font-bold text-slate-700">
                        {discount.startDate
                          ? new Date(discount.startDate).toLocaleDateString()
                          : "N/A"}{" "}
                        -{" "}
                        {discount.endDate
                          ? new Date(discount.endDate).toLocaleDateString()
                          : "Present"}
                      </td>
                      <td className="py-5">
                        <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-[10px] font-bold uppercase">
                          {discount.targetAudience || "All"}
                        </span>
                      </td>
                      <td className="py-5 text-right">
                        <div className="flex items-center justify-end gap-4">
                          <span
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold ${
                              discount.isActive
                                ? "bg-[#E6F7F0] text-[#008B50]"
                                : "bg-red-50 text-red-500"
                            }`}
                          >
                            {discount.isActive ? "Active" : "Inactive"}
                          </span>
                          <label className="switch scale-75 transform-gpu">
                            <input
                              type="checkbox"
                              checked={discount.isActive}
                              onChange={() =>
                                handleToggleDiscount(
                                  discount._id,
                                  discount.isActive,
                                )
                              }
                            />
                            <span className="slider round"></span>
                          </label>
                          <button
                            onClick={() => openDiscountScopeModal(discount)}
                            className="text-blue-400 hover:text-blue-600 transition-colors p-2"
                            title="Manage Function Scopes"
                          >
                            <FiEdit3 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteDiscount(discount._id)}
                            className="text-red-400 hover:text-red-600 transition-colors p-2"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {discounts.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-20 text-center text-slate-400 font-bold"
                      >
                        No discounts found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFreeDeliveriesTab = () => {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              Free Delivery Manager
            </h3>
            <p className="text-sm text-slate-500">
              Track and manage free delivery campaigns
            </p>
          </div>
          <button
            onClick={openFreeDeliveryModal}
            disabled={loading}
            className="bg-[#008B50] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Free Delivery
          </button>
        </div>

        <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm space-y-8">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-50">
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Campaign Name
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="pb-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                      Target
                    </th>
                    <th className="pb-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">
                      Status & Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {freeDeliveries.map((fd, idx: number) => (
                    <tr
                      key={fd._id || idx}
                      className="group hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="py-5 font-bold text-slate-700">
                        {fd.campaign || fd.name}
                      </td>
                      <td className="py-5 text-sm text-slate-500 max-w-xs truncate">
                        {fd.description || "N/A"}
                      </td>
                      <td className="py-5 text-sm font-bold text-slate-700">
                        {fd.startDate
                          ? new Date(fd.startDate).toLocaleDateString()
                          : "N/A"}{" "}
                        -{" "}
                        {fd.endDate
                          ? new Date(fd.endDate).toLocaleDateString()
                          : "Present"}
                      </td>
                      <td className="py-5">
                        <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-[10px] font-bold uppercase">
                          {fd.targetAudience || "All"}
                        </span>
                      </td>
                      <td className="py-5 text-right">
                        <div className="flex items-center justify-end gap-4">
                          <span
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold ${
                              fd.isActive
                                ? "bg-[#E6F7F0] text-[#008B50]"
                                : "bg-red-50 text-red-500"
                            }`}
                          >
                            {fd.isActive ? "Active" : "Inactive"}
                          </span>
                          <label className="switch scale-75 transform-gpu">
                            <input
                              type="checkbox"
                              checked={fd.isActive}
                              onChange={() =>
                                handleToggleFreeDelivery(fd._id, fd.isActive)
                              }
                            />
                            <span className="slider round"></span>
                          </label>
                          <button
                            onClick={() => handleDeleteFreeDelivery(fd._id)}
                            className="text-red-400 hover:text-red-600 transition-colors p-2"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {freeDeliveries.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-20 text-center text-slate-400 font-bold"
                      >
                        No free delivery campaigns found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8 min-h-screen">
      <header>
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
          Rewards Admin Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Manage campaigns, track performance, and configure reward settings
        </p>
      </header>

      <section>
        <h2 className="text-xl font-bold mb-6 text-slate-800">
          Summary Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm relative group hover:shadow-lg transition-all duration-300"
            >
              <div className="flex justify-between items-start mb-8">
                <div
                  className={`${metric.iconBg} w-12 h-12 rounded-full flex items-center justify-center shadow-lg shadow-black/5`}
                >
                  {metric.icon}
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex items-center text-sm font-bold text-green-500">
                    <BsArrowUpRight className="mr-1 stroke-1" /> {metric.change}
                  </div>
                </div>
              </div>
              <div>
                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-[0.1em] mb-2">
                  {metric.title}
                </p>
                <p className="text-3xl font-bold text-slate-800 tracking-tight">
                  {metric.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 p-1.5 rounded-2xl inline-flex flex-wrap shadow-inner border border-slate-200/50">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-8 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
              activeTab === tab
                ? "bg-white text-slate-900 shadow-md ring-1 ring-slate-200/50"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </section>

      <main>
        {activeTab === "Overview" && renderOverview()}
        {activeTab === "Modules" && renderModules()}
        {activeTab === "Gift Spin" && renderGiftSpinTab()}
        {activeTab === "Promo codes" && renderPromoCodesTab()}
        {activeTab === "Discounts" && renderDiscountsTab()}
        {activeTab === "Free Deliveries" && renderFreeDeliveriesTab()}
        {activeTab === "Gamification" && renderGamificationTab()}
      </main>

      {isModalOpen && (
        <CreateCampaignModal isVisible={isModalVisible} onClose={closeModal} />
      )}

      {isPromoModalOpen && (
        <CreatePromoCodeModal
          isVisible={isPromoModalVisible}
          onClose={closePromoModal}
        />
      )}

      {isDiscountModalOpen && (
        <CreateDiscountModal
          isVisible={isDiscountModalVisible}
          onClose={closeDiscountModal}
        />
      )}

      {discountScopeModalOpen && selectedDiscountForScope && (
        <DiscountScopeModal
          isVisible={discountScopeModalVisible}
          onClose={closeDiscountScopeModal}
          discount={selectedDiscountForScope}
        />
      )}

      {isFreeDeliveryModalOpen && (
        <CreateFreeDeliveryModal
          isVisible={isFreeDeliveryModalVisible}
          onClose={closeFreeDeliveryModal}
        />
      )}

      {isModuleModalOpen && (
        <CreateNewModuleModal
          isVisible={isModuleModalVisible}
          onClose={closeModuleModal}
          initialData={selectedModule}
          initialType={selectedModuleType}
        />
      )}

      {isPromoDetailsModalOpen && (
        <PromoCodeDetailsModal
          isVisible={isPromoDetailsModalVisible}
          isLoading={promoDetailsLoading}
          error={promoDetailsError}
          campaign={selectedPromoCampaign}
          onClose={closePromoDetailsModal}
          onDelete={() => {
            const id = selectedPromoCampaign?._id;
            if (!id) return;
            closePromoDetailsModal();
            openDeleteModal(id, "CAMPAIGN");
          }}
          onSave={async (campaignId, payload) => {
            const action = await dispatch(updateCampaign({ campaignId, payload }));
            if (updateCampaign.fulfilled.match(action)) {
              closePromoDetailsModal();
              fetchFilteredCampaigns(campaignFilter);
            }
          }}
        />
      )}

      {deleteModalOpen && (
        <DeleteConfirmationModal
          isVisible={deleteModalVisible}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title={
            itemToDeleteType === "CAMPAIGN"
              ? "Delete Promo Code"
              : itemToDeleteType === "DISCOUNT"
                ? "Delete Discount"
                : itemToDeleteType === "FREE_DELIVERY"
                  ? "Delete Free Delivery"
                  : "Delete Module"
          }
          message={
            itemToDeleteType === "CAMPAIGN"
              ? "Are you sure you want to delete this promo code? This action cannot be undone."
              : itemToDeleteType === "DISCOUNT"
                ? "Are you sure you want to delete this discount? This action cannot be undone."
                : itemToDeleteType === "FREE_DELIVERY"
                  ? "Are you sure you want to delete this free delivery campaign? This action cannot be undone."
                  : "Are you sure you want to delete this module? This action cannot be undone."
          }
        />
      )}
    </div>
  );
};

const CreatePromoCodeModal = ({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { promoCodeFunctions, loading } = useAppSelector(
    (state) => state.rewards,
  );
  const [step, setStep] = useState(1);
  const [selectedFunction, setSelectedFunction] = useState<string>("");
  const [formData, setFormData] = useState({
    fullName: "",
    promoCode: "",
    description: "",
    startDate: "",
    endDate: "",
    minimumOrderValue: 1000,
    startTime: "09:00 AM",
    endTime: "09:00 PM",
    activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
    functionIds: [] as string[],
  });

  const toTimeInputValue = (value: string): string => {
    // Accepts "09:00 AM" / "9:00 am" -> "09:00" for <input type="time">
    const match = value
      ?.trim()
      .match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (!match) return "";

    const hours12 = Number(match[1]);
    const minutes = match[2];
    const meridiem = match[3].toUpperCase();
    if (!Number.isFinite(hours12) || hours12 < 1 || hours12 > 12) return "";

    let hours24 = hours12 % 12;
    if (meridiem === "PM") hours24 += 12;

    return `${String(hours24).padStart(2, "0")}:${minutes}`;
  };

  const fromTimeInputValue = (value: string): string => {
    // Accepts "13:05" -> "01:05 PM" to match existing payload format
    const match = value?.trim().match(/^(\d{2}):(\d{2})$/);
    if (!match) return "";

    const hours24 = Number(match[1]);
    const minutes = match[2];
    if (!Number.isFinite(hours24) || hours24 < 0 || hours24 > 23) return "";

    const meridiem = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${String(hours12).padStart(2, "0")}:${minutes} ${meridiem}`;
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (step === 1 && selectedFunction) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleFunctionSelect = (funcId: string) => {
    setSelectedFunction(funcId);
    setFormData((prev) => ({ ...prev, functionIds: [funcId] }));
  };

  const handleSubmit = async () => {
    const resultAction = await dispatch(createPromoCode(formData));
    if (createPromoCode.fulfilled.match(resultAction)) {
      onClose();
      setStep(1);
      setSelectedFunction("");
      setFormData({
        fullName: "",
        promoCode: "",
        description: "",
        startDate: "",
        endDate: "",
        minimumOrderValue: 1000,
        startTime: "09:00 AM",
        endTime: "09:00 PM",
        activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
        functionIds: [],
      });
      dispatch(getAllPromoCodes({ page: 1, limit: 10 }));
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        <div className="sticky top-0 bg-white/80 backdrop-blur-md px-10 py-8 border-b border-slate-50 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              Create New Promo Code
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <FiX className="text-slate-400 w-6 h-6" />
          </button>
        </div>

        <div className="p-10 space-y-8 flex-1">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  Select Function for Promo Code
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  Choose which reward module this promo code will use
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {promoCodeFunctions
                  .filter((func: any) => func.isActive)
                  .map((func: any) => (
                    <div
                      key={func._id}
                      onClick={() => handleFunctionSelect(func._id)}
                      className={`p-8 rounded-[2rem] border-2 transition-all cursor-pointer relative ${
                        selectedFunction === func._id
                          ? "border-[#D4A017] bg-[#FFF9E6]/30"
                          : "border-slate-100 hover:border-slate-200 shadow-sm"
                      }`}
                    >
                      {selectedFunction === func._id && (
                        <div className="absolute top-6 right-6 w-6 h-6 border-2 border-[#D4A017] text-[#D4A017] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                          <FiCheck className="w-4 h-4" />
                        </div>
                      )}
                      <div className="bg-[#D4A017] p-4 rounded-2xl w-fit mb-6 text-white text-3xl">
                        <FiTag />
                      </div>
                      <h4 className="font-bold text-slate-800 text-lg mb-2">
                        {func.scope || "Promo Code"}
                      </h4>
                      <p
                        className={`text-sm font-medium ${
                          selectedFunction === func._id
                            ? "text-[#D4A017] font-bold"
                            : "text-slate-500"
                        }`}
                      >
                        {func.value
                          ? `${func.value}${
                              func.valueType === "PERCENTAGE" ? "%" : ""
                            } off`
                          : "Generate and distribute promotional discount codes"}
                      </p>
                    </div>
                  ))}
              </div>
              {promoCodeFunctions.length === 0 && (
                <div className="text-center py-12 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                  <p className="text-slate-400 font-bold mb-4">
                    No promo code functions available
                  </p>
                  <p className="text-slate-500 text-sm">
                    Create a promo code function in the Modules tab first
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Promo Code *
                </label>
                <input
                  type="text"
                  name="promoCode"
                  value={formData.promoCode}
                  onChange={handleChange}
                  placeholder="Enter promo code"
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  rows={3}
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Date*
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none text-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Date*
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none text-slate-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Time
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={toTimeInputValue(formData.startTime)}
                    onChange={(e) => {
                      const next = fromTimeInputValue(e.target.value);
                      setFormData((prev) => ({ ...prev, startTime: next }));
                    }}
                    step={60}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Time
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={toTimeInputValue(formData.endTime)}
                    onChange={(e) => {
                      const next = fromTimeInputValue(e.target.value);
                      setFormData((prev) => ({ ...prev, endTime: next }));
                    }}
                    step={60}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Minimum Order Value (₦)
                </label>
                <input
                  type="number"
                  name="minimumOrderValue"
                  value={formData.minimumOrderValue}
                  onChange={handleChange}
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-600"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Active Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          activeDays: prev.activeDays.includes(day)
                            ? prev.activeDays.filter((d) => d !== day)
                            : [...prev.activeDays, day],
                        }));
                      }}
                      className={`px-4 py-2 rounded-xl font-medium transition-all ${
                        formData.activeDays.includes(day)
                          ? "bg-[#D4A017] text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white px-10 py-8 border-t border-slate-100 flex justify-between gap-4">
          {step === 2 && (
            <button
              onClick={handleBack}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all"
            >
              Back
            </button>
          )}
          {step === 1 && <div />}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            {step === 1 && (
              <button
                onClick={handleNext}
                disabled={!selectedFunction}
                className={`px-12 py-4 rounded-xl font-bold text-white shadow-lg shadow-green-900/10 transition-all ${
                  selectedFunction
                    ? "bg-[#008B50] hover:bg-[#007041]"
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-12 py-4 rounded-xl font-bold text-white bg-[#008B50] hover:bg-[#007041] shadow-lg shadow-green-900/10 transition-all ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Creating..." : "Create Promo Code"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PromoCodeDetailsModal = ({
  isVisible,
  isLoading,
  error,
  campaign,
  onClose,
  onDelete,
  onSave,
}: {
  isVisible: boolean;
  isLoading: boolean;
  error: string | null;
  campaign: any;
  onClose: () => void;
  onDelete: () => void;
  onSave: (campaignId: string, payload: any) => Promise<void>;
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [draft, setDraft] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isVisible) return;
    setIsEditMode(false);
    setDraft(campaign ? { ...campaign } : null);
  }, [isVisible, campaign]);

  const usageList: any[] = Array.isArray(campaign?.promoCodeUsage)
    ? campaign.promoCodeUsage
    : [];
  const usageCount = usageList.length;

  // Convert "09:00 AM" / "9:00 am" → "09:00" for <input type="time">
  const toTimeInputValue = (value: string): string => {
    if (!value) return "";
    // If it's already in 24h format "HH:mm"
    if (/^\d{2}:\d{2}$/.test(value.trim())) return value.trim();
    // Try 12h format "HH:MM AM/PM"
    const match = value.trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (match) {
      const hours12 = Number(match[1]);
      const minutes = match[2];
      const meridiem = match[3].toUpperCase();
      if (!Number.isFinite(hours12) || hours12 < 1 || hours12 > 12) return "";
      let hours24 = hours12 % 12;
      if (meridiem === "PM") hours24 += 12;
      return `${String(hours24).padStart(2, "0")}:${minutes}`;
    }
    // Try ISO date string — extract time portion
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    return "";
  };

  // Convert "13:05" → "01:05 pm" for backend payload
  const fromTimeInputValue = (value: string): string => {
    const match = value?.trim().match(/^(\d{2}):(\d{2})$/);
    if (!match) return "";
    const hours24 = Number(match[1]);
    const minutes = match[2];
    if (!Number.isFinite(hours24) || hours24 < 0 || hours24 > 23) return "";
    const meridiem = hours24 >= 12 ? "pm" : "am";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${String(hours12).padStart(2, "0")}:${minutes} ${meridiem}`;
  };

  // Convert ISO date or date string → "YYYY-MM-DD" for <input type="date">
  const toDateInputValue = (value: string): string => {
    if (!value) return "";
    // Already in YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const handleSave = async () => {
    const id = campaign?._id;
    if (!id || !draft) return;
    setSaving(true);
    try {
      // Only send fields that actually changed from the original campaign
      const payload: Record<string, any> = {};

      // Extract function IDs: draft.functions is an array of objects with _id from the API
      const draftFunctionIds: string[] = Array.isArray(draft.functions)
        ? draft.functions.map((f: any) => (typeof f === "string" ? f : f._id || f.id)).filter(Boolean)
        : Array.isArray(draft.functionIds)
          ? draft.functionIds
          : [];
      const origFunctionIds: string[] = Array.isArray(campaign.functions)
        ? campaign.functions.map((f: any) => (typeof f === "string" ? f : f._id || f.id)).filter(Boolean)
        : [];
      if (JSON.stringify(draftFunctionIds) !== JSON.stringify(origFunctionIds) && draftFunctionIds.length > 0) {
        payload.functions = draftFunctionIds;
      }

      // Only include changed scalar fields
      if (draft.promoCode !== campaign.promoCode) payload.promoCode = draft.promoCode;
      if (draft.campaign !== campaign.campaign) payload.campaign = draft.campaign;
      if (draft.description !== campaign.description) payload.description = draft.description;
      if (draft.minimumOrderValue !== campaign.minimumOrderValue) payload.minimumOrderValue = draft.minimumOrderValue;
      if (draft.isActive !== campaign.isActive) payload.isActive = draft.isActive;

      // startTime / endTime: only send if the user edited them (stored in
      // "HH:MM am/pm" format by the time picker onChange handler)
      if (draft.startTime !== campaign.startTime) payload.startTime = draft.startTime;
      if (draft.endTime !== campaign.endTime) payload.endTime = draft.endTime;

      // startDate / endDate: only send if changed
      if (draft.startDate !== campaign.startDate) payload.startDate = draft.startDate;
      if (draft.endDate !== campaign.endDate) payload.endDate = draft.endDate;

      // activeDays: only send if changed
      if (JSON.stringify(draft.activeDays) !== JSON.stringify(campaign.activeDays)) {
        payload.activeDays = draft.activeDays;
      }

      if (Object.keys(payload).length === 0) {
        setSaving(false);
        return; // nothing changed
      }

      await onSave(id, payload);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible ? "opacity-100 backdrop-blur-sm" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        <div className="sticky top-0 bg-white/80 backdrop-blur-md px-10 py-8 border-b border-slate-50 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              Promo Code Details
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              View usage, edit, or delete this promo code
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <FiX className="text-slate-400 w-6 h-6" />
          </button>
        </div>

        <div className="p-10 space-y-8 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#008B50]" />
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <p className="text-red-500 font-bold">{error}</p>
              <p className="text-slate-500 text-sm mt-2">
                Please close and try again.
              </p>
            </div>
          ) : !campaign ? (
            <div className="py-12 text-center">
              <p className="text-slate-400 font-bold">No promo code selected</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-50/60 border border-slate-100 rounded-2xl p-6">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Code
                  </p>
                  <p className="text-lg font-extrabold text-slate-800 mt-2 break-all">
                    {campaign.promoCode || "—"}
                  </p>
                </div>
                <div className="bg-slate-50/60 border border-slate-100 rounded-2xl p-6">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Campaign
                  </p>
                  <p className="text-lg font-extrabold text-slate-800 mt-2 break-words">
                    {campaign.campaign || "—"}
                  </p>
                </div>
                <div className="bg-slate-50/60 border border-slate-100 rounded-2xl p-6">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Usage count
                  </p>
                  <p className="text-lg font-extrabold text-slate-800 mt-2">
                    {usageCount.toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white border border-slate-100 rounded-3xl p-8">
                <div className="flex items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Promo code info
                    </h3>
                    <p className="text-sm text-slate-500">
                      Core details for this promo code
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsEditMode((v) => !v)}
                    className="px-5 py-2.5 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all"
                  >
                    {isEditMode ? "Cancel Edit" : "Edit"}
                  </button>
                </div>

                {isEditMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Promo Code
                      </label>
                      <input
                        value={draft?.promoCode ?? ""}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), promoCode: e.target.value }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Campaign
                      </label>
                      <input
                        value={draft?.campaign ?? ""}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), campaign: e.target.value }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Description
                      </label>
                      <textarea
                        value={draft?.description ?? ""}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), description: e.target.value }))
                        }
                        rows={3}
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700 resize-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={toDateInputValue(draft?.startDate ?? "")}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), startDate: e.target.value }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={toDateInputValue(draft?.endDate ?? "")}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), endDate: e.target.value }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={toTimeInputValue(draft?.startTime ?? "")}
                        onChange={(e) => {
                          const formatted = fromTimeInputValue(e.target.value);
                          if (formatted) setDraft((p: any) => ({ ...(p || {}), startTime: formatted }));
                        }}
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={toTimeInputValue(draft?.endTime ?? "")}
                        onChange={(e) => {
                          const formatted = fromTimeInputValue(e.target.value);
                          if (formatted) setDraft((p: any) => ({ ...(p || {}), endTime: formatted }));
                        }}
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Minimum Order Value (₦)
                      </label>
                      <input
                        type="number"
                        value={draft?.minimumOrderValue ?? 0}
                        onChange={(e) =>
                          setDraft((p: any) => ({
                            ...(p || {}),
                            minimumOrderValue: Number(e.target.value || 0),
                          }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-medium text-slate-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Active
                      </label>
                      <select
                        value={String(Boolean(draft?.isActive))}
                        onChange={(e) =>
                          setDraft((p: any) => ({ ...(p || {}), isActive: e.target.value === "true" }))
                        }
                        className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none font-bold text-slate-700 appearance-none cursor-pointer"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="md:col-span-2 space-y-3">
                      <label className="text-sm font-bold text-slate-700">
                        Active Days
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
                          (day) => {
                            const isSelected = Array.isArray(draft?.activeDays) && draft.activeDays.includes(day);
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() =>
                                  setDraft((p: any) => {
                                    const current = Array.isArray(p?.activeDays) ? p.activeDays : [];
                                    const updated = current.includes(day)
                                      ? current.filter((d: string) => d !== day)
                                      : [...current, day];
                                    return { ...(p || {}), activeDays: updated };
                                  })
                                }
                                className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                                  isSelected
                                    ? "bg-[#008B50] text-white shadow-md shadow-green-900/10"
                                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                }`}
                              >
                                {day}
                              </button>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Description
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {campaign.description || "—"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Minimum order value
                      </p>
                      <p className="text-sm font-extrabold text-slate-800">
                        ₦{Number(campaign.minimumOrderValue ?? 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Start Date
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {formatUtcCalendarDate(campaign.startDate)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        End Date
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {formatUtcCalendarDate(campaign.endDate)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Start Time
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {campaign.startTime || "—"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        End Time
                      </p>
                      <p className="text-sm font-medium text-slate-700">
                        {campaign.endTime || "—"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                        Status
                      </p>
                      <p className="text-sm font-extrabold text-slate-800">
                        {campaign.isActive ? "Active" : "Inactive"}
                      </p>
                    </div>
                    {Array.isArray(campaign.activeDays) && campaign.activeDays.length > 0 && (
                      <div className="md:col-span-2 space-y-1">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                          Active Days
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {campaign.activeDays.map((day: string) => (
                            <span
                              key={day}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#E6F7F0] text-[#008B50]"
                            >
                              {day}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-100 rounded-3xl p-8">
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-slate-800">
                    Promo code usage
                  </h3>
                  <p className="text-sm text-slate-500">
                    People who used this promo code
                  </p>
                </div>

                {usageCount === 0 ? (
                  <div className="py-10 text-center text-slate-400 font-bold">
                    No usage yet
                  </div>
                ) : (
                  <div className="max-h-[320px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    <div className="space-y-3">
                      {usageList.map((u: any, idx: number) => {
                        const userObj = typeof u?.userId === "object" && u?.userId !== null ? u.userId : null;
                        const id = String(u?._id ?? userObj?._id ?? userObj?.id ?? u?.userId ?? idx);
                        const name =
                          userObj
                            ? `${userObj.firstName ?? ""} ${userObj.lastName ?? ""}`.trim() || userObj.email || "—"
                            : u?.fullName || u?.name || `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim() || "—";
                        const email = userObj?.email || u?.email || u?.userEmail || "";
                        const usageCount_item = u?.usageCount ?? null;
                        const usedAt = u?.usedAt || u?.createdAt || u?.dateUsed || "";

                        return (
                          <div
                            key={id}
                            className="flex items-start justify-between gap-4 p-4 rounded-2xl border border-slate-100 bg-slate-50/40"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-extrabold text-slate-800 truncate">
                                {name}
                              </p>
                              {email ? (
                                <p className="text-xs text-slate-500 truncate">
                                  {email}
                                </p>
                              ) : null}
                              {userObj?.phoneNumber ? (
                                <p className="text-xs text-slate-400 truncate">
                                  {userObj.phoneNumber}
                                </p>
                              ) : null}
                            </div>
                            <div className="flex-shrink-0 text-right space-y-1">
                              {usageCount_item != null ? (
                                <p className="text-xs font-bold text-[#008B50] bg-green-50 px-2.5 py-1 rounded-lg whitespace-nowrap">
                                  Used {usageCount_item}×
                                </p>
                              ) : null}
                              {usedAt ? (
                                <p className="text-xs font-bold text-slate-500 whitespace-nowrap">
                                  {new Date(usedAt).toLocaleString()}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="bg-white px-10 py-8 border-t border-slate-100 flex justify-between gap-4">
          <button
            type="button"
            onClick={onDelete}
            className="px-8 py-4 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition-all"
          >
            Delete
          </button>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isEditMode || saving}
              className={`px-12 py-4 rounded-xl font-bold text-white shadow-lg shadow-green-900/10 transition-all ${
                isEditMode && !saving
                  ? "bg-[#008B50] hover:bg-[#007041]"
                  : "bg-slate-300 cursor-not-allowed"
              }`}
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CreateCampaignModal = ({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.rewards);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    userName: "",
    description: "",
    startDate: "",
    endDate: "",
    minimumOrderValue: 0,
    startTime: "09:00 AM",
    endTime: "09:00 PM",
    activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
    functionIds: ["69767b585be064288d1f23f6"], // Placeholder scope ID from example
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter((d) => d !== day)
        : [...prev.activeDays, day],
    }));
  };

  const handleSubmit = async () => {
    // Convert activeDays to lowercase to match backend enum expectations
    const payload = {
      ...formData,
      activeDays: formData.activeDays,
    };
    const resultAction = await dispatch(createCampaign(payload));
    if (createCampaign.fulfilled.match(resultAction)) {
      onClose();
      setStep(1);
    }
  };

  const nextStep = () => {
    if (step === 4) {
      handleSubmit();
    } else {
      setStep((s) => Math.min(s + 1, 4));
    }
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-10 py-8 border-b border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
                Create New Campaign
              </h2>
              <p className="text-slate-400 font-medium">Step {step} of 4</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <FiX className="text-slate-400 w-6 h-6" />
            </button>
          </div>
          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
            <div
              className="bg-[#008B50] h-full transition-all duration-500 ease-out"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-10">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <h3 className="text-xl font-bold text-slate-800">
                Campaign Details
              </h3>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Campaign Details *
                  </label>
                  <input
                    type="text"
                    name="userName"
                    value={formData.userName}
                    onChange={handleChange}
                    placeholder="e.g New year Mega Rewards"
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Campaign Description*
                  </label>
                  <textarea
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your campaign and what makes it special..."
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all resize-none font-medium"
                  />
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      Start Date*
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleChange}
                        className="w-full p-4 pl-4 rounded-xl border border-slate-200 bg-slate-50 outline-none text-slate-400 font-medium"
                      />
                      <BsCalendar4Event className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      End Date*
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleChange}
                        className="w-full p-4 pl-4 rounded-xl border border-slate-200 bg-slate-50 outline-none text-slate-400 font-medium"
                      />
                      <BsCalendar4Event className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div className="space-y-6 pt-4 border-t border-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#E6F7F0] p-2 rounded-lg text-[#008B50]">
                      <FiCheckCircle className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Daily Operating Hours
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        Start Time *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="startTime"
                          value={formData.startTime}
                          onChange={handleChange}
                          className="w-full p-4 rounded-xl bg-slate-50/50 border border-slate-200 outline-none font-medium text-slate-400"
                        />
                        <BsCalendar4Event className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold ml-1 uppercase">
                        Campaign starts at {formData.startTime}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700">
                        End Time*
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          name="endTime"
                          value={formData.endTime}
                          onChange={handleChange}
                          className="w-full p-4 rounded-xl bg-slate-50/50 border border-slate-200 outline-none font-medium text-slate-400"
                        />
                        <BsCalendar4Event className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#FFF4E6] p-6 rounded-[1.5rem] border border-[#FF9500]/20 flex items-start gap-4">
                    <div className="text-[#FF9500] mt-1">
                      <FiInfo className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[#FF9500] text-sm leading-none mb-2">
                        Campaign Duration: {formData.startTime} -{" "}
                        {formData.endTime}
                      </h4>
                      <p className="text-[#FF9500]/80 text-xs font-medium">
                        Users can only participate during these hours
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 pt-4 border-t border-slate-50">
                  <div className="flex items-center gap-3">
                    <FiClock className="w-6 h-6 text-slate-800" />
                    <h3 className="text-lg font-bold text-slate-800">
                      Active Days
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {[
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ].map((day) => (
                      <button
                        key={day}
                        onClick={() => handleDayToggle(day)}
                        className={`px-8 py-5 rounded-2xl font-bold text-sm transition-all ${
                          formData.activeDays.includes(day)
                            ? "bg-[#1E5D3E] text-white shadow-lg shadow-green-900/20"
                            : "bg-slate-100 text-slate-300"
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 font-bold ml-1">
                    Campaign active on: {formData.activeDays.join(", ")}
                  </p>
                </div>

                <div className="space-y-6 pt-4 border-t border-slate-50">
                  <h4 className="font-bold text-slate-800 text-sm">
                    Launch Immediately
                  </h4>
                  <div className="bg-white p-6 rounded-[1.5rem] border border-slate-100 flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-medium">
                      Campaign will be active as soon as it's created
                    </p>
                    <label className="switch">
                      <input type="checkbox" defaultChecked />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  Select Campaign Type
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  Choose which reward module this campaign will use
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {[
                  {
                    id: "sw",
                    title: "Spin and Win",
                    desc: "Let users spin the win for instant rewards and discounts",
                    icon: <BsDiamondFill />,
                    color: "bg-[#004D40]",
                    active: true,
                  },
                  {
                    id: "pc",
                    title: "Post Check Out Gifts",
                    desc: "Category based gifts after order completion",
                    icon: <FiGift />,
                    color: "bg-[#E39100]",
                  },
                  {
                    id: "fd",
                    title: "Free Delivery",
                    desc: "Offer free delivery vouchers as redeemable rewards",
                    icon: <FiTruck />,
                    color: "bg-[#005AB5]",
                  },
                  {
                    id: "pr",
                    title: "Promo Codes",
                    desc: "Generate and Distribute promotional discount code",
                    icon: <FiTag />,
                    color: "bg-[#D4A017]",
                  },
                ].map((type) => (
                  <div
                    key={type.id}
                    className={`p-10 rounded-[2rem] border-2 transition-all cursor-pointer relative ${
                      type.active
                        ? "border-[#008B50] bg-[#E6F7F0]/20"
                        : "border-slate-100 hover:border-slate-200 shadow-sm"
                    }`}
                  >
                    {type.active && (
                      <div className="absolute top-8 right-8 w-6 h-6 border-2 border-[#008B50] text-[#008B50] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                        <FiCheck className="w-4 h-4" />
                      </div>
                    )}
                    <div
                      className={`${type.color} p-4 rounded-2xl w-fit mb-6 text-white text-3xl`}
                    >
                      {type.icon}
                    </div>
                    <h4 className="font-bold text-slate-800 text-lg mb-2">
                      {type.title}
                    </h4>
                    <p
                      className={`text-sm font-medium ${
                        type.active
                          ? "text-[#008B50] font-bold"
                          : "text-slate-500"
                      }`}
                    >
                      {type.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-xl font-bold text-[#008B50] mb-1 leading-none uppercase tracking-wide">
                  Configure Campaign Settings
                </h3>
                <p className="text-slate-500 text-sm font-medium mt-1">
                  Set up reward parameters and targeting options
                </p>
              </div>

              <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-10">
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">
                    Spin Wheel Probability Distribution
                  </h4>
                  <p className="text-xs text-slate-400 mb-8 font-medium">
                    Adjust the probability (%) for each reward segment
                  </p>
                  <div className="space-y-8">
                    {[
                      { label: "Discount10", val: 20 },
                      { label: "Discount20", val: 15 },
                      { label: "Free Delivery", val: 15 },
                      { label: "Cash Back", val: 15 },
                      { label: "Wallet Bonus", val: 10 },
                      { label: "Try Again", val: 25 },
                    ].map((item, i) => (
                      <div key={i} className="space-y-3">
                        <div className="flex justify-between text-sm font-bold">
                          <span className="text-slate-800">{item.label}</span>
                          <span className="text-[#008B50]">{item.val}%</span>
                        </div>
                        <div className="h-3.5 bg-[#E6F7F0]/50 rounded-full relative overflow-hidden">
                          <div
                            className="bg-[#008B50] h-full"
                            style={{ width: `${item.val}%` }}
                          />
                          <div
                            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-[#E6F7F0] rounded-full shadow-sm"
                            style={{ left: `calc(${item.val}% - 8px)` }}
                          />
                        </div>
                      </div>
                    ))}
                    <div className="p-8 bg-[#FFF9F3] rounded-[1.5rem] border border-[#FF9500]/10">
                      <p className="text-[#FF9500] font-extrabold text-lg text-left">
                        Total: 100%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-10 rounded-[2.5rem] border border-slate-100 shadow-sm space-y-10">
                <h4 className="text-lg font-bold text-[#008B50]">
                  Targeting & Limits
                </h4>

                <div className="space-y-6">
                  <label className="text-sm font-bold text-slate-700">
                    Target Audience
                  </label>
                  <div className="grid grid-cols-4 gap-4">
                    {["All", "New", "Returning", "Vip"].map((tag) => (
                      <button
                        key={tag}
                        className={`py-5 rounded-2xl font-bold text-sm transition-all ${
                          tag === "New"
                            ? "bg-[#008B50] text-white shadow-lg shadow-green-900/10"
                            : "bg-slate-50 text-slate-500"
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      Max Participants
                    </label>
                    <div className="relative">
                      <select className="w-full p-6 pr-12 rounded-2xl border border-slate-100 bg-slate-50 outline-none appearance-none font-bold text-slate-600">
                        <option>10000</option>
                      </select>
                      <FiChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">
                      Campaign Budget
                    </label>
                    <div className="relative">
                      <select className="w-full p-6 pr-12 rounded-2xl border border-slate-100 bg-slate-50 outline-none appearance-none font-bold text-slate-600">
                        <option>₦500</option>
                      </select>
                      <FiChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-2xl font-bold text-[#008B50] mb-1">
                  Review Campaign
                </h3>
                <p className="text-slate-500 text-sm font-medium">
                  Review all details before launching your campaign
                </p>
              </div>

              <div className="bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <h4 className="text-xl font-bold text-slate-800">
                    {formData.userName || "Untitled Campaign"}
                  </h4>
                  <span className="px-5 py-2 rounded-xl text-sm font-bold bg-[#008B50] text-white">
                    Ready
                  </span>
                </div>
                <p className="text-slate-600 mb-6 font-medium text-lg">
                  {formData.description || "No description provided"}
                </p>
                <div className="flex items-center gap-8">
                  <div className="flex items-center gap-3 text-slate-500">
                    <div className="bg-slate-50 p-2 rounded-lg">
                      <BsCalendar4Event className="text-[#008B50] text-lg" />
                    </div>
                    <span className="font-bold text-[#008B50]">
                      {formData.startDate || "N/A"} -{" "}
                      {formData.endDate || "Present"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500">
                    <div className="bg-slate-50 p-2 rounded-lg">
                      <FiClock className="text-[#FF9500] text-lg" />
                    </div>
                    <span className="font-bold text-[#FF9500]">
                      {formData.startTime} - {formData.endTime}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <h4 className="text-xl font-bold text-slate-800">
                  Configuration Summary
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { label: "Campaign Type", value: "Spin and Win" },
                    {
                      label: "Min Order Value",
                      value: `₦${formData.minimumOrderValue}`,
                    },
                    {
                      label: "Active Days",
                      value: `${formData.activeDays.length} days`,
                    },
                    { label: "Function", value: "10% Discount" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-slate-50/50 p-8 rounded-[1.5rem] border border-slate-100/50"
                    >
                      <p className="text-slate-500 font-bold text-sm mb-2 uppercase tracking-wide">
                        {item.label}
                      </p>
                      <p className="text-xl font-extrabold text-slate-800">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#E6F7F0] p-6 rounded-[1.5rem] border border-[#008B50]/20 flex items-start gap-4">
                <div className="bg-white rounded-full p-1.5 flex items-center justify-center border border-[#008B50]/30 shadow-sm">
                  <FiCheck className="text-[#008B50] w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-[#008B50] text-lg">
                    Ready to Launch
                  </h4>
                  <p className="text-[#008B50]/80 font-medium">
                    Your campaign is configured and ready to go live. Click
                    "Launch Campaign" to activate it immediately.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white px-10 py-8 border-t border-slate-100 flex justify-between items-center">
          <button
            onClick={prevStep}
            disabled={step === 1}
            className={`px-10 py-4 rounded-xl font-bold text-slate-500 transition-all ${
              step === 1
                ? "opacity-0 invisible"
                : "bg-slate-100 hover:bg-slate-200"
            }`}
          >
            Back
          </button>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-10 py-4 rounded-xl font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={nextStep}
              disabled={loading}
              className={`px-14 py-4 rounded-xl font-bold text-white bg-[#008B50] hover:bg-[#007041] shadow-lg shadow-green-900/10 transition-all ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {step === 4
                ? loading
                  ? "Launching..."
                  : "Launch Campaign"
                : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CreateDiscountModal = ({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { discountFunctions, loading } = useAppSelector(
    (state) => state.rewards,
  );
  const [step, setStep] = useState(1);
  const [selectedFunctions, setSelectedFunctions] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    campaign: "",
    description: "",
    startDate: "",
    endDate: "",
    startTime: "09:00 AM",
    endTime: "09:00 PM",
    activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
    targetAudience: "all",
    functionId: [] as string[],
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter((d) => d !== day)
        : [...prev.activeDays, day],
    }));
  };

  const handleFunctionSelect = (functionId: string) => {
    setSelectedFunctions((prev) => {
      if (prev.includes(functionId)) {
        const updated = prev.filter((id) => id !== functionId);
        setFormData((f) => ({ ...f, functionId: updated }));
        return updated;
      }
      if (prev.length >= 4) {
        toast.error("You can only select exactly 4 functions");
        return prev;
      }
      const updated = [...prev, functionId];
      setFormData((f) => ({ ...f, functionId: updated }));
      return updated;
    });
  };

  const handleNext = () => {
    if (step === 1) {
      if (selectedFunctions.length !== 4) {
        toast.error("Please select exactly 4 functions to proceed");
        return;
      }
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleSubmit = async () => {
    // Convert activeDays to lowercase to match backend enum expectations
    const payload = {
      ...formData,
      activeDays: formData.activeDays,
    };
    const resultAction = await dispatch(createDiscount(payload));
    if (createDiscount.fulfilled.match(resultAction)) {
      onClose();
      setStep(1);
      setSelectedFunctions([]);
      setFormData({
        campaign: "",
        description: "",
        startDate: "",
        endDate: "",
        startTime: "09:00 AM",
        endTime: "09:00 PM",
        activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
        targetAudience: "all",
        functionId: [],
      });
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        <div className="sticky top-0 bg-white/80 backdrop-blur-md px-10 py-8 border-b border-slate-50 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              Create New Discount
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <FiX className="text-slate-400 w-6 h-6" />
          </button>
        </div>

        <div className="p-10 space-y-8 flex-1">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  Select Functions for Discount
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  Choose exactly 4 reward functions for this campaign (
                  {selectedFunctions.length}/4 selected)
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {discountFunctions
                  .filter((func: any) => func.isActive)
                  .map((func: any) => (
                    <div
                      key={func._id}
                      onClick={() => handleFunctionSelect(func._id)}
                      className={`p-8 rounded-[2rem] border-2 transition-all cursor-pointer relative ${
                        selectedFunctions.includes(func._id)
                          ? "border-[#008B50] bg-[#E6F7F0]/30"
                          : "border-slate-100 hover:border-slate-200 shadow-sm"
                      }`}
                    >
                      {selectedFunctions.includes(func._id) && (
                        <div className="absolute top-6 right-6 w-6 h-6 border-2 border-[#008B50] text-[#008B50] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                          <FiCheck className="w-4 h-4" />
                        </div>
                      )}
                      <div className="bg-[#008B50] p-4 rounded-2xl w-fit mb-6 text-white text-3xl">
                        <FiTag />
                      </div>
                      <h4 className="font-bold text-slate-800 text-lg mb-2">
                        {func.scope || "Discount"}
                      </h4>
                      <p
                        className={`text-sm font-medium ${
                          selectedFunctions.includes(func._id)
                            ? "text-[#008B50] font-bold"
                            : "text-slate-500"
                        }`}
                      >
                        {func.value
                          ? `${func.value}${
                              func.valueType === "PERCENTAGE" ? "%" : ""
                            } discount`
                          : "Let users spin the win for instant rewards and discounts"}
                      </p>
                    </div>
                  ))}
              </div>
              {discountFunctions.length === 0 && (
                <div className="text-center py-12 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                  <p className="text-slate-400 font-bold mb-4">
                    No discount functions available
                  </p>
                  <p className="text-slate-500 text-sm">
                    Create a discount function in the Modules tab first
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Discount Name (Campaign)*
                </label>
                <input
                  type="text"
                  name="campaign"
                  value={formData.campaign}
                  onChange={handleChange}
                  placeholder="e.g Sunday Flash Sale"
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Description*
                </label>
                <textarea
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your discount..."
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Time
                  </label>
                  <input
                    type="text"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    placeholder="9:00 AM"
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Time
                  </label>
                  <input
                    type="text"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    placeholder="9:00 PM"
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Target Audience
                </label>
                <select
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleChange}
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                >
                  <option value="all">All Users</option>
                  <option value="new">New Users</option>
                  <option value="returning">Returning Users</option>
                </select>
              </div>
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-700">
                  Active Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        formData.activeDays.includes(day)
                          ? "bg-[#008B50] text-white"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white px-10 py-8 border-t border-slate-100 flex justify-between gap-4">
          {step === 2 && (
            <button
              onClick={handleBack}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all"
            >
              Back
            </button>
          )}
          {step === 1 && <div />}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            {step === 1 && (
              <button
                onClick={handleNext}
                disabled={selectedFunctions.length !== 4}
                className={`px-12 py-4 rounded-xl font-bold text-white shadow-lg shadow-green-900/10 transition-all ${
                  selectedFunctions.length === 4
                    ? "bg-[#008B50] hover:bg-[#007041]"
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-12 py-4 rounded-xl font-bold text-white bg-[#008B50] hover:bg-[#007041] shadow-lg shadow-green-900/10 transition-all ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Creating..." : "Create Discount"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SpinAndWin;

const CreateFreeDeliveryModal = ({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { freeDeliveryFunctions, loading } = useAppSelector(
    (state) => state.rewards,
  );
  const [step, setStep] = useState(1);
  const [selectedFunction, setSelectedFunction] = useState<string>("");
  const [formData, setFormData] = useState({
    campaign: "",
    description: "",
    startDate: "",
    endDate: "",
    startTime: "9:00 am",
    endTime: "9:00 pm",
    activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
    targetAudience: "all",
    functionId: [] as string[],
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayToggle = (day: string) => {
    setFormData((prev) => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter((d) => d !== day)
        : [...prev.activeDays, day],
    }));
  };

  const handleFunctionSelect = (functionId: string) => {
    setSelectedFunction(functionId);
    setFormData((prev) => ({ ...prev, functionId: [functionId] }));
  };

  const handleNext = () => {
    if (step === 1 && selectedFunction) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const handleSubmit = async () => {
    // Convert activeDays to lowercase to match backend enum expectations
    const payload = {
      ...formData,
      activeDays: formData.activeDays,
    };
    const resultAction = await dispatch(createFreeDelivery(payload));
    if (createFreeDelivery.fulfilled.match(resultAction)) {
      onClose();
      setStep(1);
      setSelectedFunction("");
      setFormData({
        campaign: "",
        description: "",
        startDate: "",
        endDate: "",
        startTime: "9:00 am",
        endTime: "9:00 pm",
        activeDays: ["Monday", "Tuesday", "Wednesday", "Thursday"],
        targetAudience: "all",
        functionId: [],
      });
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        <div className="sticky top-0 bg-white/80 backdrop-blur-md px-10 py-8 border-b border-slate-50 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              Create Free Delivery Campaign
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <FiX className="text-slate-400 w-6 h-6" />
          </button>
        </div>

        <div className="p-10 space-y-8 flex-1">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  Select Function for Free Delivery
                </h3>
                <p className="text-slate-500 text-sm mt-1">
                  Choose which reward module this campaign will use
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                {freeDeliveryFunctions
                  .filter((func: any) => func.isActive)
                  .map((func: any) => (
                    <div
                      key={func._id}
                      onClick={() => handleFunctionSelect(func._id)}
                      className={`p-8 rounded-[2rem] border-2 transition-all cursor-pointer relative ${
                        selectedFunction === func._id
                          ? "border-[#0047AB] bg-[#F2F7FF]/30"
                          : "border-slate-100 hover:border-slate-200 shadow-sm"
                      }`}
                    >
                      {selectedFunction === func._id && (
                        <div className="absolute top-6 right-6 w-6 h-6 border-2 border-[#0047AB] text-[#0047AB] rounded-full flex items-center justify-center animate-in zoom-in duration-300">
                          <FiCheck className="w-4 h-4" />
                        </div>
                      )}
                      <div className="bg-[#0047AB] p-4 rounded-2xl w-fit mb-6 text-white text-3xl">
                        <FiTruck />
                      </div>
                      <h4 className="font-bold text-slate-800 text-lg mb-2">
                        {func.scope || "Free Delivery"}
                      </h4>
                      <p
                        className={`text-sm font-medium ${
                          selectedFunction === func._id
                            ? "text-[#0047AB] font-bold"
                            : "text-slate-500"
                        }`}
                      >
                        {func.value
                          ? `${func.value}${
                              func.valueType === "PERCENTAGE" ? "%" : ""
                            } off delivery`
                          : "Offer free delivery vouchers as redeemable rewards"}
                      </p>
                    </div>
                  ))}
              </div>
              {freeDeliveryFunctions.length === 0 && (
                <div className="text-center py-12 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                  <p className="text-slate-400 font-bold mb-4">
                    No free delivery functions available
                  </p>
                  <p className="text-slate-500 text-sm">
                    Create a free delivery function in the Modules tab first
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Campaign Name*
                </label>
                <input
                  type="text"
                  name="campaign"
                  value={formData.campaign}
                  onChange={handleChange}
                  placeholder="e.g Weekend Free Delivery"
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Description*
                </label>
                <textarea
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your offer..."
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] outline-none transition-all resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Start Time
                  </label>
                  <input
                    type="text"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    placeholder="9:00 am"
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    End Time
                  </label>
                  <input
                    type="text"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    placeholder="9:00 pm"
                    className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Target Audience
                </label>
                <select
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleChange}
                  className="w-full p-4 rounded-xl border border-slate-200 bg-slate-50 outline-none"
                >
                  <option value="all">All Users</option>
                  <option value="new">New Users</option>
                  <option value="returning">Returning Users</option>
                </select>
              </div>
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-700">
                  Active Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        formData.activeDays.includes(day)
                          ? "bg-[#008B50] text-white"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white px-10 py-8 border-t border-slate-100 flex justify-between gap-4">
          {step === 2 && (
            <button
              onClick={handleBack}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all"
            >
              Back
            </button>
          )}
          {step === 1 && <div />}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="px-8 py-4 rounded-xl font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            {step === 1 && (
              <button
                onClick={handleNext}
                disabled={!selectedFunction}
                className={`px-12 py-4 rounded-xl font-bold text-white shadow-lg shadow-green-900/10 transition-all ${
                  selectedFunction
                    ? "bg-[#008B50] hover:bg-[#007041]"
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`px-12 py-4 rounded-xl font-bold text-white bg-[#008B50] hover:bg-[#007041] shadow-lg shadow-green-900/10 transition-all ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Creating..." : "Create Campaign"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const CreateNewModuleModal = ({
  isVisible,
  onClose,
  initialData,
  initialType,
}: {
  isVisible: boolean;
  onClose: () => void;
  initialData?: any;
  initialType?: "PROMO" | "DISCOUNT" | "FREE_DELIVERY" | "MANUAL";
}) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.rewards);
  const [step, setStep] = useState(1);
  const [type, setType] = useState<
    "PROMO" | "DISCOUNT" | "FREE_DELIVERY" | "MANUAL"
  >(initialType || "DISCOUNT");

  const [formData, setFormData] = useState({
    scope: "Percentage Discount",
    value: "",
    image: "",
    isActive: true,
  });

  useEffect(() => {
    if (isVisible) {
      if (initialData) {
        setStep(2);
        if (initialType) setType(initialType);
        setFormData({
          scope: initialData.scope || "Percentage Discount",
          value: initialData.value?.toString() || "",
          image: initialData.image || "",
          isActive: initialData.isActive ?? true,
        });
      } else {
        setStep(1);
        if (initialType) setType(initialType);
        setFormData({
          scope: "Percentage Discount",
          value: "",
          image: "",
          isActive: true,
        });
      }
    }
  }, [isVisible, initialData, initialType]);

  const handleTypeSelect = (selected: any) => {
    setType(selected);
    if (selected === "FREE_DELIVERY") {
      setFormData((prev) => ({ ...prev, scope: "Free Delivery" }));
    } else if (selected === "DISCOUNT" || selected === "PROMO") {
      setFormData((prev) => ({ ...prev, scope: "Percentage Discount" }));
    }
    setStep(2);
  };

  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const resultAction = await dispatch(uploadFile({ file, type: "image" }));
      if (uploadFile.fulfilled.match(resultAction)) {
        setFormData((prev) => ({ ...prev, image: resultAction.payload.url }));
        toast.success("Image uploaded successfully");
      } else {
        toast.error(
          (resultAction.payload as string) || "Failed to upload image",
        );
      }
    } catch (err) {
      toast.error("An error occurred during upload");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    let resultAction;
    const payload = { ...formData, value: Number(formData.value) };

    if (type === "DISCOUNT") {
      if (initialData) {
        resultAction = await dispatch(
          updateDiscountFunction({
            functionId: initialData._id,
            payload,
          }),
        );
      } else {
        resultAction = await dispatch(createDiscountFunction(payload));
      }
    } else if (type === "FREE_DELIVERY") {
      const freePayload = {
        scope: formData.scope,
        isActive: formData.isActive,
      };
      if (initialData) {
        resultAction = await dispatch(
          updateFreeDeliveryFunction({
            functionId: initialData._id,
            payload: freePayload,
          }),
        );
      } else {
        resultAction = await dispatch(createFreeDeliveryFunction(freePayload));
      }
    } else {
      // PROMO or MANUAL — no image
      const { image, ...promoPayload } = payload;
      if (initialData) {
        resultAction = await dispatch(
          updatePromoCodeFunction({
            functionId: initialData._id,
            payload: promoPayload,
          }),
        );
      } else {
        resultAction = await dispatch(createPromoCodeFunction(promoPayload));
      }
    }

    if (
      createDiscountFunction.fulfilled.match(resultAction) ||
      updateDiscountFunction.fulfilled.match(resultAction) ||
      createFreeDeliveryFunction.fulfilled.match(resultAction) ||
      updateFreeDeliveryFunction.fulfilled.match(resultAction) ||
      createPromoCodeFunction.fulfilled.match(resultAction) ||
      updatePromoCodeFunction.fulfilled.match(resultAction)
    ) {
      onClose();
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-[2.5rem] shadow-2xl transition-all duration-500 flex flex-col ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-md px-10 py-8 border-b border-slate-50 flex justify-between items-center z-10">
          <div className="flex items-center gap-6">
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight">
              {step === 1
                ? "Select Module Type"
                : initialData
                  ? "Update Module"
                  : "Create New Module"}
            </h2>
            {step === 2 && (
              <div className="flex items-center gap-3 ml-4">
                <span className="text-sm font-bold text-[#008B50]">
                  {formData.isActive ? "Active" : "Inactive"}
                </span>
                <label className="switch scale-75 transform-gpu">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        isActive: !prev.isActive,
                      }))
                    }
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <FiX className="text-slate-400 w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-10 space-y-10 flex-1">
          {step === 1 ? (
            <div className="grid grid-cols-3 gap-6">
              {[
                {
                  id: "DISCOUNT",
                  label: "Discount Function",
                  icon: <FiTag className="w-8 h-8" />,
                  color: "bg-[#FF9500]",
                },
                {
                  id: "FREE_DELIVERY",
                  label: "Free Delivery",
                  icon: <FiTruck className="w-8 h-8" />,
                  color: "bg-[#0047AB]",
                },
                {
                  id: "PROMO",
                  label: "Promo Function",
                  icon: <FiGift className="w-8 h-8" />,
                  color: "bg-[#004D40]",
                },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => handleTypeSelect(t.id)}
                  className="flex flex-col items-center gap-4 p-8 rounded-2xl border-2 border-slate-100 hover:border-[#008B50] hover:bg-[#E6F7F0]/30 transition-all group"
                >
                  <div
                    className={`${t.color} p-4 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform`}
                  >
                    {t.icon}
                  </div>
                  <span className="font-bold text-lg text-slate-700">
                    {t.label}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">
                  Scope*
                </label>
                <div className="relative">
                  {type === "FREE_DELIVERY" ? (
                    <input
                      type="text"
                      value={formData.scope}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          scope: e.target.value,
                        }))
                      }
                      className="w-full p-6 rounded-2xl border border-slate-100 bg-slate-50 outline-none font-bold text-slate-600 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] transition-all"
                      placeholder="e.g. Free Delivery"
                    />
                  ) : (
                    <>
                      <select
                        value={formData.scope}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            scope: e.target.value,
                          }))
                        }
                        className="w-full p-6 pr-12 rounded-2xl border border-slate-100 bg-slate-50 outline-none appearance-none font-bold text-slate-600 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] transition-all"
                      >
                        <option value="Percentage Discount">
                          Percentage Discount
                        </option>
                        <option value="Fixed Amount">Fixed Amount</option>
                      </select>
                      <FiChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
                    </>
                  )}
                </div>
              </div>

              {type !== "FREE_DELIVERY" && (
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Value *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Enter value"
                      value={formData.value}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          value: e.target.value,
                        }))
                      }
                      className="w-full p-6 pr-12 rounded-2xl border border-slate-100 bg-slate-50 outline-none font-bold text-slate-600 focus:bg-white focus:ring-2 focus:ring-[#008B50]/20 focus:border-[#008B50] transition-all"
                    />
                    <span className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-800 font-bold text-xl">
                      {formData.scope === "Percentage Discount" ? "%" : "₦"}
                    </span>
                  </div>
                </div>
              )}

              {type === "DISCOUNT" && (
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">
                    Upload Image*
                  </label>
                  <label className="border-2 border-dashed border-slate-200 rounded-[2.5rem] p-12 flex flex-col items-center justify-center gap-4 bg-slate-50/50 hover:bg-slate-50 transition-colors cursor-pointer group relative overflow-hidden">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />

                    {isUploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#008B50]"></div>
                        <p className="text-slate-500 font-bold">Uploading...</p>
                      </div>
                    ) : formData.image ? (
                      <div className="flex flex-col items-center gap-4">
                        <img
                          src={formData.image}
                          alt="Preview"
                          className="w-32 h-32 object-contain rounded-2xl"
                        />
                        <div className="flex flex-col items-center">
                          <p className="text-[#008B50] font-bold">
                            Image Uploaded
                          </p>
                          <p className="text-[10px] text-slate-400">
                            Click to change
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <FiUpload className="text-slate-400 w-8 h-8" />
                        </div>
                        <p className="text-slate-500 font-bold">Upload Image</p>
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white/80 backdrop-blur-md px-10 py-8 border-t border-slate-50 flex gap-4">
          {step === 2 && (
            <>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`flex-1 bg-[#008B50] text-white py-5 rounded-2xl font-bold hover:bg-[#007041] transition-all shadow-lg shadow-green-900/10 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading
                  ? "Processing..."
                  : initialData
                    ? "Update Function"
                    : "Create Function"}
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-[#E6F7F0] text-[#008B50] py-5 rounded-2xl font-bold hover:bg-[#d5eedf] transition-all"
              >
                Cancel
              </button>
            </>
          )}
          {step === 1 && (
            <button
              onClick={onClose}
              className="w-full bg-[#E6F7F0] text-[#008B50] py-5 rounded-2xl font-bold hover:bg-[#d5eedf] transition-all"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

const DeleteConfirmationModal = ({
  isVisible,
  onClose,
  onConfirm,
  title,
  message,
}: {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}) => {
  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-sm rounded-[2rem] shadow-2xl transition-all duration-500 overflow-hidden transform ${
          isVisible
            ? "scale-100 translate-y-0 opacity-100"
            : "scale-95 translate-y-10 opacity-0"
        }`}
      >
        <div className="bg-red-50 p-6 flex justify-center items-center">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg transform -translate-y-2">
            <FiTrash2 className="w-10 h-10 text-red-500" />
          </div>
        </div>
        <div className="p-8 text-center space-y-4">
          <h3 className="text-2xl font-bold text-slate-800">{title}</h3>
          <p className="text-slate-500 font-medium leading-relaxed">
            {message}
          </p>
        </div>
        <div className="flex gap-3 px-8 pb-8">
          <button
            onClick={onClose}
            className="flex-1 bg-slate-100 text-slate-600 py-3.5 rounded-xl font-bold hover:bg-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3.5 rounded-xl font-bold hover:shadow-lg hover:shadow-red-500/30 hover:-translate-y-0.5 transition-all"
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Discount Scope Modal - Manage function scopes for a discount
const DiscountScopeModal = ({
  isVisible,
  onClose,
  discount,
}: {
  isVisible: boolean;
  onClose: () => void;
  discount: any;
}) => {
  const dispatch = useAppDispatch();
  const { discountFunctions, loading } = useAppSelector(
    (state) => state.rewards,
  );
  const [localScopes, setLocalScopes] = useState<any[]>([]);

  useEffect(() => {
    // The server returns discount.functions as an array of { scopeId, isActive, isAdminActive, _id }
    // isActive = function is part of the discount (always true)
    // isAdminActive = admin-toggled state (the one we care about)
    const activeFunctions = discount?.functions || [];
    const scopes = discountFunctions.map((func: any) => {
      const match = activeFunctions.find(
        (f: any) => f.scopeId === func._id || f.scopeId?._id === func._id,
      );
      return {
        ...func,
        isAdminActive: match ? match.isAdminActive === true : false,
      };
    });
    setLocalScopes(scopes);
  }, [discount, discountFunctions]);

  const handleToggle = async (scopeId: string, currentStatus: boolean) => {
    const resultAction = await dispatch(
      toggleDiscountFunctionScope({
        discountId: discount._id,
        scopeId,
        isAdminActive: !currentStatus,
      }),
    );
    if (toggleDiscountFunctionScope.fulfilled.match(resultAction)) {
      // Update local state immediately
      setLocalScopes((prev) =>
        prev.map((s) =>
          s._id === scopeId ? { ...s, isAdminActive: !currentStatus } : s,
        ),
      );
      // Refresh parent discount data so next modal open has correct state
      dispatch(getAllDiscounts());
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center transition-all duration-300 ${
        isVisible
          ? "opacity-100 backdrop-blur-sm"
          : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`relative bg-white w-full max-w-lg rounded-[2rem] shadow-2xl transition-all duration-500 ${
          isVisible ? "scale-100 translate-y-0" : "scale-95 translate-y-10"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#008B50] to-[#007041] px-8 py-4 rounded-t-[2rem]">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white">
                Manage Function Scopes
              </h2>
              <p className="text-white/80 text-sm mt-1">
                {discount?.campaign || "Discount"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <FiX className="text-white w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-500 text-sm mb-2">
            Toggle which discount functions are available for this promotion:
          </p>

          <div className="space-y-2 max-h-[370px] overflow-y-auto">
            {localScopes.map((scope: any) => (
              <div
                key={scope._id}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  scope.isAdminActive
                    ? "border-[#008B50] bg-[#E6F7F0]/30"
                    : "border-slate-100 bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#008B50] p-3 rounded-xl text-white">
                      <FiTag className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">
                        {scope.scope || "Discount"}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {scope.value
                          ? `${scope.value}${scope.valueType === "PERCENTAGE" ? "%" : "₦"} off`
                          : "Fixed discount"}
                      </p>
                    </div>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={scope.isAdminActive}
                      onChange={() =>
                        handleToggle(scope._id, scope.isAdminActive)
                      }
                      disabled={loading}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>
            ))}
            {localScopes.length === 0 && (
              <div className="text-center py-6 text-slate-400">
                No discount functions available.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-2 border-t border-slate-100">
          <button
            onClick={onClose}
            className="w-full bg-slate-100 text-slate-600 py-4 rounded-2xl font-bold hover:bg-slate-200 transition-all"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
